/**
 * custom-size-flow.js — modal step flow for the Custom Size Selector.
 * Vanilla JS, no dependencies. Depends on window.CustomSizing.computeSize
 * (assets/size-algorithm.js) for the sizing result.
 *
 * Rebuilt to match the "Size Finder Panel Options" design handoff
 * (editorial serif direction: Domaine Display + Proxima Nova, navy/gold/
 * off-white palette). Key differences from the first pass:
 *
 *  - Mode select is a numbered row list (I Know My Size / Find My Size /
 *    Book a Call), not card choices. Size guide and Compare my size are not
 *    rows: they open from buttons inside the first two paths, in a drawer
 *    that slides over the panel from the right (see openDrawer).
 *  - Build (garment length) is NOT asked as its own question in the
 *    custom-measurements path - it's derived from height by the sizing
 *    algorithm (see size-algorithm.js) and shown alongside the
 *    recommended size, adjustable via "Shorter / Longer" on the review
 *    screen. It's still asked explicitly in the Standard Sizes path,
 *    since that path never runs the algorithm.
 *  - Every selection screen (standard size/build grids, chest, collar,
 *    waist, fit) uses select-then-confirm: pick a value, then press
 *    Next. Deliberately NOT pre-selecting a default the way the design
 *    mockup's demo screenshots show - defaulting to an arbitrary value
 *    risks a customer clicking through without actually confirming their
 *    own measurement, which is a real data-quality problem for a
 *    made-to-order garment. Next stays disabled until something is
 *    actually chosen.
 *  - Review screen adds "Not quite right?" quick adjusters (Size down/up,
 *    Shorter/Longer) that nudge the recommendation without re-running the
 *    whole questionnaire, an expandable read-only numbers panel with a
 *    cm/inches toggle, and an email capture field.
 *
 *    IMPORTANT — the email field does not currently send anything. Emailing
 *    a customer their specific recommendation is a transactional send with
 *    dynamic content; the connected Spoks integration only exposes
 *    campaign/flow/segment/form *management* tools (nothing that submits a
 *    contact or triggers a flow from arbitrary storefront JS), and even if
 *    it did, an MCP tool is only reachable by the agent, never by code
 *    running in a customer's browser. Shipping this as a real send needs a
 *    server-side piece - e.g. a Shopify app proxy or webhook that calls
 *    Spoks' (or another provider's) public API. Until that exists, Send
 *    just confirms inline; see submitEmailCapture() below.
 *
 * Build support: some products carry a second variant option (e.g. "Build":
 * Short/Regular/Long) alongside Size. When the section/snippet config
 * reports hasBuildOption, findVariant() matches on Size AND Build together
 * - matching on Size alone would silently pick whichever Build happens to
 * be first for that Size.
 *
 * On add-to-basket the flow posts the variant and its line item properties
 * to /cart/add.js and shows the basket inside the panel (see addToCart and
 * renderBasket) rather than navigating to the cart page. The hidden product
 * form rendered by the section/snippet is kept as the fallback if that
 * request cannot be made at all.
 *
 * Integration note: root.csfOpen / root.csfClose are exposed on the flow's
 * root element so a page can drive the flow from a CTA button that lives
 * outside this markup (see hide_own_trigger in custom-size-flow.liquid).
 *
 * Sitewide hash-trigger note: any link anywhere on the page (in a section
 * that has no idea this flow exists - e.g. a plain "Button link" field on
 * Image + text / Image banner blocks) can also open the panel just by
 * having its URL/link field set to "#find-your-size" - no per-section code
 * changes required. See the document-level click listener near the top of
 * this file, right after the DOMContentLoaded/initFlow wiring.
 */
(function () {
  'use strict';

  /* Step order and question content are driven by the flow's Questions list
   * (see QUESTIONS in initFlow). These are the fallback used when a product
   * has no Size flow assigned, or its flow lists no questions - identical to
   * the set that was hardcoded here previously.
   *
   * inputKey is the contract with size-algorithm.js: it names which
   * normalised input the answer fills, and is therefore also what selects the
   * unit handling below. A question can be reworded, reordered or dropped in
   * the admin; it cannot be given an inputKey the algorithm doesn't read. */
  var DEFAULT_QUESTIONS = [
    { inputKey: 'heightCm', kind: 'number', title: 'How tall are you?', hint: null },
    { inputKey: 'weightKg', kind: 'number', title: 'How much do you weigh?', hint: 'This, with your height, gives us your overall build.' },
    { inputKey: 'shoeSizeEu', kind: 'number', title: 'What is your shoe size?', hint: null },
    { inputKey: 'chestRange', kind: 'choice_grid', title: 'What is your chest size?', hint: 'Measured around the fullest part of your chest.' },
    { inputKey: 'collarIn', kind: 'choice_grid', title: 'What is your collar size?', hint: 'In inches, measured around the base of your neck.' },
    { inputKey: 'waistIn', kind: 'choice_grid', title: 'What is your waist size?', hint: 'In inches, as you would buy trousers.' },
    { inputKey: 'fit', kind: 'choice_list', title: 'How do you like your fit?', hint: null }
  ];

  var DEFAULT_OPTIONS = {
    collarIn: [14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 20, 20.5, 21],
    waistIn: [28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42],
    fit: ['slim', 'regular', 'relaxed']
  };
  var DEFAULT_SUBLABELS = {
    fit: ['Closer through the body', 'Classic fit with comfortable ease', 'Looser drape, roomier through chest and waist']
  };

  var LS_UNITS = 'csf:units';
  var LS_SIZES = 'csf:savedSizes';

  /* Only measurements that come from a published spec are shown. Shoulders,
   * Collar, Waist and Sleeve length were removed: they were derived from
   * ratios rather than the pattern, and checked against the Hampden tech pack
   * were out by 2-6 cm. Add a row back only when the value behind it is a
   * real graded point of measure. */
  var MEASUREMENT_LABELS = {
    chestCm: 'Chest'
  };

  /* Chest choices: inch range <-> alpha size, index-aligned.
   *
   * FALLBACK ONLY - used when the product has no Size flow assigned, or its
   * flow leaves chest_ranges / size_ladder empty. Live values come from the
   * flow metaobject (see chestSizesFor() in initFlow).
   *
   * These bands were previously shifted one full step up (36-38 = XS through
   * 48-50 = 2XL), which disagreed with the published Hampden size guide by a
   * whole size - a 41" chest resolved to M here while the size chart on the
   * same page said L - and left 46-48" (7% of men) in a gap no option covered.
   * Corrected to match the size guide: 34-36 = XS through 44-46 = 2XL,
   * contiguous, covering 88.6% of adult males (ANSUR II, n=4082). */
  var DEFAULT_CHEST_SIZES = [
    ['34-36', 'XS'], ['36-38', 'S'], ['38-40', 'M'],
    ['40-42', 'L'], ['42-44', 'XL'], ['44-46', '2XL']
  ];
  var DEFAULT_SIZE_LADDER = ['XS', 'S', 'M', 'L', 'XL', '2XL'];

  var DEFAULT_LIMITS = {
    heightCm: [120, 230],
    weightKg: [35, 220],
    shoeSizeEu: [34, 52]
  };

  /* Unit toggles per input. A question's own Unit toggles field overrides the
   * labels; the conversion maths stays keyed on inputKey, since the algorithm
   * requires cm/kg/EU regardless of what the customer typed in. */
  var DEFAULT_UNITS = {
    heightCm: [{ id: 'cm', label: 'cm' }, { id: 'ftin', label: 'ft/in' }],
    weightKg: [{ id: 'kg', label: 'kg' }, { id: 'lbs', label: 'lbs' }],
    shoeSizeEu: [{ id: 'eu', label: 'EU' }, { id: 'uk', label: 'UK' }, { id: 'us', label: 'US' }]
  };

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-csf]').forEach(initFlow);
  });

  // Sitewide-safe open hook: lets a CTA button in ANY section - one that
  // has no knowledge of this flow's markup, e.g. the plain "Button link"
  // field on Image + text / Image banner blocks - open this panel with no
  // code changes of its own. Editors just set that button's link field to
  // "#find-your-size" in the theme editor. Uses the link's parsed .hash
  // (not a raw string match on href) so it works whether the field holds
  // a bare "#find-your-size" or a full page URL ending in that hash.
  // Registered once at the document level rather than per-root during
  // initFlow, and resolves the panel root at click time via
  // document.querySelector('[data-csf]') - safe no-op (does nothing, lets
  // the click proceed normally) on any page where the panel doesn't exist.
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href]');
    if (!link || link.hash !== '#find-your-size') return;
    var root = document.querySelector('[data-csf]');
    if (!root || typeof root.csfOpen !== 'function') return;
    e.preventDefault();
    root.csfOpen();
  });

  function initFlow(root) {
    var config = JSON.parse(root.querySelector('[data-csf-config]').textContent);

    /* Waitlist mode: the product is not on sale yet (product metafield
     * custom.waitlist_active, alongside custom.size_flow_active). The panel
     * runs exactly as it does for a product in stock, but every path that
     * would end in the basket ends in a name + email form instead, and
     * nothing is ever posted to /cart/add. Variants on a product in
     * development carry no stock, so availability is not a gate here: there
     * is nothing to sell out of. */
    var WAITLIST = !!config.waitlist;
    // 'Continue', not 'Join the waitlist': one more screen follows, the form itself.
    var ADD_LABEL = WAITLIST ? 'Continue' : 'Add to basket';
    var ADD_STATUS = WAITLIST ? 'waitlist_size_chosen' : 'added_to_basket';
    function sellable(variant) { return !!variant && (WAITLIST || variant.available); }

    // Per-product sizing, from the Size flow metaobject assigned to this
    // product. Both shadow the module-level defaults for this flow only, so a
    // product with no flow assigned behaves exactly as before.
    //
    // SIZE_LADDER matters beyond labelling: the review screen's Size up /
    // Size down adjusters walk it. Left at the XS-2XL default, a product that
    // only runs S-XL would let a customer nudge the recommendation onto a size
    // that has no variant - findVariant() then returns null and submitCustom()
    // silently falls back to "first available", adding a size they never chose.
    var SIZE_LADDER = (config.sizeLadder && config.sizeLadder.length)
      ? config.sizeLadder.slice()
      : DEFAULT_SIZE_LADDER;

    // Index-aligned pairing of the flow's chest_ranges to its size_ladder. If
    // the two lists are different lengths we pair as far as both run rather
    // than inventing entries - a half-configured flow degrades to fewer
    // choices, never to wrong ones.
    var CHEST_SIZES = (function () {
      var ranges = config.chestRanges;
      if (!ranges || !ranges.length) return DEFAULT_CHEST_SIZES;
      var n = Math.min(ranges.length, SIZE_LADDER.length);
      var out = [];
      for (var i = 0; i < n; i++) out.push([ranges[i], SIZE_LADDER[i]]);
      return out;
    })();

    /* The flow's "Alpha toggle label" field is no longer read: neither the
     * chest question nor the Standard sizes screen offers a size-letter view
     * of the chest ranges any more. The field is left in the metaobject
     * rather than removed, since deleting a definition field deletes its
     * stored values on every entry.
     */

    // The flow's questions, in order. Step ids ARE inputKeys, so the step
    // sequence, the progress bar and the algorithm payload all stay in step
    // with whatever the admin has configured.
    /* Every product asks the same questions, in the same order: the set in
     * DEFAULT_QUESTIONS, which is the Hampden's. A flow's own Questions list
     * no longer decides WHICH questions are asked - the knitwear flows listed
     * only five - but where it has an entry for one of these it still supplies
     * the wording, options and limits. Age is not in the set and is never
     * asked, whatever a flow lists.
     *
     * Done here rather than by editing the Size flow metaobjects because those
     * are store data shared with every theme, including the live one. */
    var CONFIGURED_QUESTIONS = {};
    (config.questions || []).forEach(function (q) { CONFIGURED_QUESTIONS[q.inputKey] = q; });
    var QUESTIONS = DEFAULT_QUESTIONS.map(function (d) {
      return CONFIGURED_QUESTIONS[d.inputKey] || d;
    });
    var CUSTOM_STEPS = QUESTIONS.map(function (q) { return q.inputKey; });
    var QUESTION_BY_KEY = {};
    QUESTIONS.forEach(function (q) { QUESTION_BY_KEY[q.inputKey] = q; });

    function q(key) { return QUESTION_BY_KEY[key] || {}; }

    // Next step in the configured order, or 'loading' once the last question
    // is answered. Replaces the previous hardcoded goTo('weight') chain, which
    // broke as soon as a question was reordered or removed.
    function advance(fromKey) {
      var i = CUSTOM_STEPS.indexOf(fromKey);
      if (i === -1 || i === CUSTOM_STEPS.length - 1) {
        goTo('loading');
        computeResult();
        return;
      }
      goTo(CUSTOM_STEPS[i + 1]);
    }

    function limitsFor(key) {
      var cfg = q(key);
      var d = DEFAULT_LIMITS[key] || [-Infinity, Infinity];
      return [
        cfg.min != null && cfg.min !== '' ? Number(cfg.min) : d[0],
        cfg.max != null && cfg.max !== '' ? Number(cfg.max) : d[1]
      ];
    }

    // 'cm:cm' / 'ftin:ft/in' entries from the admin, else the built-in set.
    function unitsFor(key) {
      var raw = q(key).units;
      if (!raw || !raw.length) return DEFAULT_UNITS[key] || null;
      return raw.map(function (entry) {
        var bits = String(entry).split(':');
        return { id: bits[0].trim(), label: (bits[1] || bits[0]).trim() };
      });
    }

    function optionsFor(key) {
      var cfg = q(key);
      if (cfg.options && cfg.options.length) return cfg.options;
      return DEFAULT_OPTIONS[key] || [];
    }

    function sublabelsFor(key) {
      var cfg = q(key);
      if (cfg.sublabels && cfg.sublabels.length) return cfg.sublabels;
      return DEFAULT_SUBLABELS[key] || [];
    }

    // Finished flat chest for a given size, from the flow's Size chart.
    // Needed here as well as in size-algorithm.js because the review screen's
    // Size up / Size down adjusters change the recommended size after the
    // algorithm has run - without this the panel keeps showing the originally
    // recommended size's measurement against a different size's name.
    /* Published specs print as given. round1() snaps to the nearest half
     * centimetre, which turned a specified 27.75 cm armhole into 28 - fine for
     * an estimate, wrong for a figure presented as the specification the
     * garment is made to. Inches are derived, so one decimal there. */
    function fmtSpec(cm) {
      if (state.numbersUnit === 'cm') return trimNum(Math.round(cm * 100) / 100) + ' cm';
      return trimNum(Math.round((cm / 2.54) * 10) / 10) + ' in';
    }
    function trimNum(n) { return String(parseFloat(n.toFixed(2))); }

    function measurementsForSize(size) {
      if (!size) return [];
      var out = [];
      MEASUREMENT_ROWS.forEach(function (r) {
        if (r.values[size] != null) out.push({ label: r.label, cm: r.values[size] });
      });
      return out;
    }

    function chartChestFor(size) {
      var list = config.sizeChart;
      if (!list || !list.length || !size) return null;
      for (var i = 0; i < list.length; i++) {
        var bits = String(list[i]).split(':');
        if (bits[0].trim() === size) {
          var n = Number(bits[1]);
          return isFinite(n) ? n : null;
        }
      }
      return null;
    }

    /* ------------------------------------------------------ room to move

     * How much room a size leaves over this customer's own chest, said in
     * plain numbers on the result screen.
     *
     * The recommendation is only as good as its arithmetic, and until this
     * was shown, nothing on screen let a customer catch it being wrong. It
     * follows the size they are actually looking at, so sizing up or down
     * with the adjusters updates it. */
    function bodyChestCm() {
      var r = state.result;
      if (r && isFinite(r.bodyChestCm)) return r.bodyChestCm;
      /* Results saved before the algorithm returned the body chest, and any
       * flow whose questions are answered differently, fall back to the
       * stated range. */
      var range = state.inputs.chestRange;
      if (!range) return null;
      var parts = String(range).split('-').map(Number);
      if (parts.length !== 2 || !isFinite(parts[0]) || !isFinite(parts[1])) return null;
      return (parts[0] + parts[1]) / 2 * 2.54;
    }

    function fitBandFor(roomCm) {
      var slim = Number(config.easeSlim);
      var regular = Number(config.easeRegular);
      var relaxed = Number(config.easeRelaxed);
      if (!isFinite(slim) || !isFinite(regular) || !isFinite(relaxed)) return '';
      /* Named against the flow's own ease settings rather than the fit the
       * customer picked: a grading step is wider than the gap between slim
       * and regular, so the size they end up with is not always the fit they
       * asked for, and saying otherwise would be the lie this line exists to
       * prevent. */
      /* No tolerance at the bottom. A coat with less room in it than the
       * slim cut allows is one that will not close over a shirt, and calling
       * that "about our slim fit" is the error this line exists to catch.
       * Loose has no such consequence, so it keeps a 2 cm tolerance. */
      if (roomCm < slim) return 'tighter than our slim fit';
      if (roomCm > relaxed + 2) return 'looser than our relaxed fit';
      var best = 'regular';
      var bestDelta = Math.abs(regular - roomCm);
      if (Math.abs(slim - roomCm) < bestDelta) { best = 'slim'; bestDelta = Math.abs(slim - roomCm); }
      if (Math.abs(relaxed - roomCm) < bestDelta) { best = 'relaxed'; }
      return 'about our ' + best + ' fit';
    }

    function roomDetail(size) {
      var chartChest = chartChestFor(size);
      var body = bodyChestCm();
      if (chartChest == null || body == null) return '';
      var room = Math.round((chartChest * 2 - body) * 10) / 10;
      if (room <= 0) {
        /* "leaves -6 cm of room" is arithmetic, not English. */
        return size + ' measures ' + trimNum(Math.abs(room)) +
          ' cm less than your chest, so it will not fasten.';
      }
      var text = size + ' leaves ' + trimNum(room) + ' cm of room over your chest';
      var band = fitBandFor(room);
      if (band) text += ', ' + band;
      text += '.';
      /* When the waist rather than the chest decided the size, the room over
       * the chest reads as too much for the fit they asked for. Say why, on
       * the size that was recommended, so it does not look like a mistake. */
      var res = state.result;
      if (res && res.sizedOnWaist && size === res.recommendedSize) {
        text += ' We have sized you on your waist rather than your chest, so it fastens comfortably all the way down.';
      }
      if (SIZE_LADDER.length && size === SIZE_LADDER[SIZE_LADDER.length - 1]) {
        text += ' It is the largest size we make.';
      }
      return text;
    }

    /* The flow's finished-measurement table: 'Label|v1|v2|...', one value per
     * size in SIZE_LADDER order. Rows whose value count does not match the
     * ladder are dropped rather than displayed against the wrong size - a
     * silently misaligned row is worse than a missing one. */
    var MEASUREMENT_ROWS = (function () {
      var raw = config.measurements;
      if (!raw || !raw.length) return [];
      var rows = [];
      for (var i = 0; i < raw.length; i++) {
        var bits = String(raw[i]).split('|');
        if (bits.length !== SIZE_LADDER.length + 1) {
          console.warn('[csf] measurement row skipped, expected ' +
            SIZE_LADDER.length + ' values for "' + bits[0] + '", got ' + (bits.length - 1));
          continue;
        }
        var values = {};
        for (var k = 0; k < SIZE_LADDER.length; k++) {
          var n = Number(bits[k + 1]);
          if (isFinite(n)) values[SIZE_LADDER[k]] = n;
        }
        rows.push({ label: bits[0].trim(), values: values });
      }
      return rows;
    })();

    /* Two guides, not one.
     *
     * The first version of this shared a single field between the Size guide
     * screen and the Compare screen, on the reasoning that writing the
     * instructions once and showing them twice avoided duplication. That was
     * wrong: they are not the same instructions. Telling someone to wrap a
     * tape around the fullest part of their chest, and telling them to lay a
     * coat flat and measure straight across 2.5 cm below the armhole, teach
     * two different measurements that happen to share a label. Showing the
     * garment version on a screen headed "How to measure" would have taught
     * people to measure themselves the way you measure a coat.
     *
     * measureGuide  -> body measurement, Size guide screen, optional diagram
     * compareGuide  -> garment measured flat, Compare screen, no diagram
     */
    function parseGuide(raw, withImage) {
      var out = {};
      if (!raw || !raw.length) return out;
      raw.forEach(function (entry) {
        var bits = String(entry).split('|');
        if (bits.length < 2) return;
        var label = bits[0].trim();
        if (!withImage) {
          out[label] = { text: bits.slice(1).join('|').trim(), image: null };
          return;
        }
        out[label] = {
          text: (bits[1] || '').trim(),
          image: bits[2] ? sizedImage(bits[2].trim()) : null
        };
      });
      return out;
    }

    /* Shopify CDN images are served at whatever size you ask for. The source
     * diagrams are 1024x1536; the panel is 420px wide at most, so requesting
     * the full file would be roughly ten times the bytes needed on a phone. */
    function sizedImage(url) {
      if (!url) return null;
      if (url.indexOf('cdn.shopify.com') === -1) return url;
      if (url.indexOf('width=') !== -1) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=600';
    }

    var MEASURE_GUIDE = parseGuide(config.measureGuide, true);
    var COMPARE_GUIDE = parseGuide(config.compareGuide, false);

    /* Which measurement points the Compare screen asks for. Defaults to the
     * first four published rows, because asking for all seven of the coat's
     * points would lose most people before they finished. Chest first: it is
     * the point that decides the size, and the one people measure most
     * reliably. */
    var COMPARE_POINTS = (function () {
      var wanted = config.comparePoints;
      var available = MEASUREMENT_ROWS.map(function (r) { return r.label; });
      if (wanted && wanted.length) {
        var kept = wanted.filter(function (label) { return available.indexOf(label) !== -1; });
        wanted.forEach(function (label) {
          if (available.indexOf(label) === -1) {
            console.warn('[csf] compare point "' + label + '" has no matching measurement row; ignored.');
          }
        });
        if (kept.length) return kept;
      }
      return available.slice(0, 4);
    })();

    /* Relative importance when scoring a customer's garment against ours.
     * Chest decides whether a garment fits at all; shoulders decide whether it
     * looks right; length and sleeve are adjustable-ish preferences. Equal
     * weighting would let a 4 cm sleeve difference outvote a 4 cm chest
     * difference, which is not how a coat fits. */
    var COMPARE_WEIGHTS = { chest: 3, shoulder: 2, shoulders: 2 };
    function compareWeight(label) {
      var key = String(label).toLowerCase().split(' ')[0];
      return COMPARE_WEIGHTS[key] || 1;
    }

    var panel = root.querySelector('[data-csf-panel]');
    var trigger = root.querySelector('[data-csf-open]');
    var body = root.querySelector('[data-csf-body]');
    var backBtn = root.querySelector('[data-csf-back]');
    var closeBtn = root.querySelector('[data-csf-close]');
    var progress = root.querySelector('[data-csf-progress]');
    var progressBar = root.querySelector('[data-csf-progress-bar]');
    var form = root.querySelector('[data-csf-form]');
    var variantInput = root.querySelector('[data-csf-variant-id]');
    var propsContainer = root.querySelector('[data-csf-properties]');

    // The panel relies on position:fixed to stay pinned to the viewport
    // regardless of scroll - but position:fixed is only relative to the
    // viewport when NO ancestor has a CSS transform (a transformed
    // ancestor becomes the containing block instead, per spec). This
    // theme's product info column carries Dawn's own scroll-reveal
    // animation classes (scroll-trigger animate--slide-in), which apply a
    // transform to .product__info-wrapper - an ancestor of this panel once
    // product-media-gallery.liquid moves .csf into .pinfo-right. Moving
    // the panel to a direct child of <body> sidesteps that entirely,
    // regardless of what ends up wrapping it elsewhere on the page. This
    // happens AFTER all the querySelector calls above (which rely on
    // panel's contents still being reachable from `root`), but before
    // anything below wires up listeners or reads panel.hidden/geometry.
    if (panel && panel.parentNode && document.body) {
      document.body.appendChild(panel);
    }

    /* The drawer: Size guide and Compare my size.
     *
     * Reference material the customer dips into from I Know My Size or Find My
     * Size and then puts away, so it is a layer over the panel rather than a
     * step in it: it slides in from the right, and closing it slides it back
     * out to reveal the step they were on, with whatever they had entered
     * still there.
     *
     * A sibling of the panel on <body>, with the same fixed geometry, rather
     * than a child of it. The panel is its own scroll container and carries a
     * transform, so a child could be neither pinned to the viewport nor
     * scrolled independently of the step underneath. */
    var drawer = el('div', 'csf__drawer');
    drawer.hidden = true;
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('data-lenis-prevent', '');
    var drawerHead = el('div', 'csf__drawer-head');
    var drawerClose = el('button', 'csf__drawer-close');
    drawerClose.type = 'button';
    drawerClose.appendChild(el('span', '', 'Close'));
    var drawerCloseGlyph = el('span', 'csf__drawer-close-x', '×');
    drawerCloseGlyph.setAttribute('aria-hidden', 'true');
    drawerClose.appendChild(drawerCloseGlyph);
    drawerHead.appendChild(drawerClose);
    var drawerBody = el('div', 'csf__drawer-body');
    drawer.appendChild(drawerHead);
    drawer.appendChild(drawerBody);
    if (document.body) document.body.appendChild(drawer);

    /* Reserve room for bottom chrome.
     * -----------------------------------------------------------------
     * The panel is full-viewport height, so its CTA row sits hard against
     * the bottom edge - exactly where cookie consent banners, the Shopify
     * theme preview bar and chat launchers live. Shopify's consent banner
     * carries z-index 2000000 against this panel's 9999 and so covers the
     * row outright: Back and Next were simply not clickable for any visitor
     * who had not yet accepted cookies. Next appeared to work only because
     * Enter in a number field also advances; Back has no keyboard path.
     *
     * Raising the panel above the banner would be the wrong fix - consent
     * has to stay reachable. Measure whatever is anchored to the bottom and
     * pad the panel by that much instead. */
    /* Read from a custom property rather than the panel's own padding, because
     * the padding itself is now zero: the space lives on .csf__body so that it
     * survives overflow. The property is redefined at the mobile breakpoint, and
     * this is read on every resize, so it stays correct across both. */
    function panelPadBottom() {
      var v = parseFloat(getComputedStyle(panel).getPropertyValue('--csf-pad-bottom'));
      return isFinite(v) ? v : 24;
    }

    function reserveBottomChrome() {
      var vh = window.innerHeight;
      var reserve = 0;
      var nodes = document.body ? document.body.children : [];
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (el === panel || el === drawer || el.contains(panel)) continue;
        var cs = window.getComputedStyle(el);
        if (cs.position !== 'fixed') continue;
        if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') continue;
        if (parseInt(cs.zIndex, 10) < 0) continue;      // parked behind the page
        var r = el.getBoundingClientRect();
        if (!r.height || !r.width) continue;
        if (r.bottom < vh - 8 || r.top >= vh) continue;  // not bottom-anchored
        if (r.height > vh * 0.6) continue;               // full-screen scrim, not chrome
        if (r.height > reserve) reserve = r.height;
      }
      /* On .csf__body, not on the panel. The panel is the scroll container, and
       * a scroll container's bottom padding is not painted after overflowing
       * content, so everything set here was silently discarded on precisely the
       * screens tall enough to need it. */
      panel.style.setProperty('padding-bottom', '0px');
      /* Published as a custom property that .csf__body::after consumes, not as
       * padding. .csf__body is a flex item with a height fixed by the panel, so
       * its content overflows past its own box: padding on it lands at the box
       * edge, level with the top of the overflow, rather than after the last
       * thing on screen. A trailing pseudo-element is part of the flow, so it
       * moves down with the content and reserves the room where it is wanted. */
      body.style.setProperty('--csf-space-bottom', (panelPadBottom() + reserve) + 'px');
      drawerBody.style.setProperty('--csf-space-bottom', (panelPadBottom() + reserve) + 'px');
    }

    // Re-measure when the viewport changes and when anything is added,
    // removed or toggled on <body> - the consent banner disappears on accept,
    // and the panel should reclaim the space immediately.
    var reserveQueued = false;
    function queueReserve() {
      if (reserveQueued) return;
      reserveQueued = true;
      window.requestAnimationFrame(function () {
        reserveQueued = false;
        if (!panel.hidden) reserveBottomChrome();
      });
    }
    window.addEventListener('resize', queueReserve);
    if (window.MutationObserver && document.body) {
      new MutationObserver(queueReserve).observe(document.body, {
        childList: true, attributes: true,
        attributeFilter: ['style', 'class', 'hidden']
      });
    }

    var state = {
      step: 'mode',
      history: [],
      units: loadJSON(LS_UNITS) || { height: 'cm', weight: 'kg', shoe: 'eu', collar: 'in', waist: 'in' },
      inputs: {
        heightCm: null, weightKg: null, shoeSizeEu: null,
        chestIdx: null, chestRange: null, chestAlpha: null,
        collarIn: null, waistIn: null, fit: null,
        buildValue: null,        // Standard-sizes path only
        standardSizeValue: null  // Standard-sizes path only
      },
      result: null,          // the algorithm's own answer. NEVER mutated after
                             // computeResult, so the capture can always report
                             // what was recommended as distinct from what was
                             // taken. { recommendedSize, recommendedBuild, measurements }
      chosen: null,          // what the customer has settled on: { size, build,
                             // chestCm }. Starts as a copy of the recommendation
                             // and is what the size/length adjusters move.
      sizeName: '',
      numbersExpanded: false,
      numbersUnit: 'cm',
      emailValue: '',
      emailStatus: null,      // null | 'sending' | 'confirmed' | 'invalid' | 'failed'
      waitlistSize: null,     // waitlist mode: the size the customer settled on
      waitlistName: '',
      waitlistEmail: '',
      waitlistStatus: null,   // null | 'sending' | 'invalid' | 'failed' | 'stalled'
      waitlistNeedsSize: false, // the form on screen includes the size picker
      compare: {},            // Compare my size: point label -> cm entered
      compareUnit: 'cm',
      guideSize: null,        // which size the Size guide screen is showing
      guideTab: 'measure',    // 'measure' | 'chart': which half of the Size guide
      guideJump: null,        // a How to measure label to scroll to, once
      compareResult: null,    // { size, rows: [{label, theirsCm, oursCm, deltaCm}] }
      drawer: null,           // null | 'guide' | 'compare' | 'compareResult'
      fromCompare: false,     // the build step was reached from a comparison
      adding: false,          // an add-to-basket request is in flight
      cart: null,             // /cart.js, once something has been added
      basketError: null,
      variantError: null      // set when a chosen combination has no variant
    };

    /* ------------------------------------------------------------- capture
     *
     * Everything the panel learns is posted to the endpoint in the theme
     * setting "Sizing capture endpoint" (a Google Apps Script web app that
     * writes a row and emails the studio). Before this existed the only record
     * of a sizing session was the line item properties, which meant a record
     * existed only if the customer actually added to basket - every abandoned
     * flow, which is most of them and the interesting ones, left no trace.
     *
     * Design rules, in order of importance:
     *  1. Capture must never be able to break the panel. Every send is
     *     fire-and-forget inside a try/catch with a .catch() on the promise,
     *     and nothing in the UI waits on a response.
     *  2. One row per session, updated in place, not one row per event. The
     *     session id is generated when the panel is first opened and carried
     *     through result, adjustment, email and add-to-basket, so the Sheet
     *     shows a journey rather than disconnected fragments.
     *  3. Content-Type is text/plain so the browser issues no CORS preflight.
     *     Apps Script cannot answer an OPTIONS request, so a JSON content type
     *     would fail before the POST was ever made. The body is still JSON.
     *  4. keepalive lets the add-to-basket send survive the page navigation
     *     that immediately follows form.submit().
     */
    var capture = (function () {
      var endpoint = config.captureEndpoint || '';
      var secret = config.captureSecret || '';
      var sessionId = null;
      var adjustTimer = null;

      function newSessionId() {
        var rand = (window.crypto && window.crypto.randomUUID)
          ? window.crypto.randomUUID().split('-')[0] + window.crypto.randomUUID().split('-')[1]
          : Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 6);
        return Date.now().toString(36) + '-' + rand;
      }

      function ensureSession() {
        if (!sessionId) sessionId = newSessionId();
        return sessionId;
      }

      function device() {
        var w = window.innerWidth || 0;
        return (w < 750 ? 'mobile' : w < 1100 ? 'tablet' : 'desktop') + ' ' + w + 'px';
      }

      function send(status, extra) {
        if (!endpoint) return;
        var payload = {
          secret: secret,
          sessionId: ensureSession(),
          status: status,
          product: config.productTitle || '',
          productHandle: config.productHandle || '',
          flow: config.flowName || '',
          pageUrl: location.href,
          referrer: document.referrer || '',
          device: device()
        };
        Object.keys(extra || {}).forEach(function (k) { payload[k] = extra[k]; });

        try {
          fetch(endpoint, {
            method: 'POST',
            // text/plain keeps this a CORS "simple request": no preflight.
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify(payload),
            keepalive: true,
            mode: 'cors'
          }).catch(function (err) {
            console.warn('[csf] capture failed (ignored):', err);
          });
        } catch (err) {
          console.warn('[csf] capture threw (ignored):', err);
        }
      }

      /* Adjustments arrive in bursts as the customer taps size up / size down.
       * Debounced so the Sheet records where they settled, not every keystroke
       * of indecision. */
      function sendAdjustDebounced(extra) {
        clearTimeout(adjustTimer);
        adjustTimer = setTimeout(function () { send('size_adjusted', extra); }, 1500);
      }

      /* Capture failing is invisible by design: nothing in the UI waits on it,
       * so a misconfigured endpoint looks exactly like a working one until you
       * check the spreadsheet and find it empty. This says so at startup, and
       * window.csfDiagnose() below tests the whole chain on demand. */
      if (!endpoint) {
        console.warn('[csf] Sizing capture is OFF: no endpoint configured. ' +
          'Set the shop metafield custom.sizing_capture_endpoint to the Apps Script web app URL. ' +
          'Run window.csfDiagnose() for a full check.');
      } else if (!secret) {
        console.warn('[csf] Sizing capture endpoint is set but the shared secret is empty. ' +
          'The Apps Script will reject every request as unauthorised.');
      }

      return { send: send, sendAdjustDebounced: sendAdjustDebounced, ensureSession: ensureSession,
               id: function () { return sessionId; },
               endpoint: function () { return endpoint; },
               secret: function () { return secret; } };
    })();

    /* A one-command check of the capture chain, for the browser console.
     *
     * Every link in this chain fails quietly on its own: an unset metafield,
     * an Apps Script deployed to the wrong audience, a script whose secret does
     * not match. Each looks identical from the customer's side, which is a
     * nuisance to debug by elimination. This posts a clearly marked test row
     * and reports which link broke.
     *
     * The row it writes has status 'diagnostic' and product 'DIAGNOSTIC TEST',
     * so it is obvious in the sheet and easy to delete. */
    window.csfDiagnose = async function () {
      var out = { endpointConfigured: !!capture.endpoint(), secretConfigured: !!capture.secret() };
      console.log('[csf] endpoint:', capture.endpoint() || '(not set)');
      console.log('[csf] secret:', capture.secret() ? '(set)' : '(NOT SET)');

      if (!out.endpointConfigured) {
        console.error('[csf] FAIL: no endpoint. Nothing can be saved. ' +
          'Set shop metafield custom.sizing_capture_endpoint.');
        return out;
      }

      try {
        var res = await fetch(capture.endpoint(), {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify({
            secret: capture.secret(),
            sessionId: 'diagnostic-' + Date.now(),
            status: 'diagnostic',
            product: 'DIAGNOSTIC TEST',
            productHandle: config.productHandle || '',
            pageUrl: location.href
          })
        });
        out.httpStatus = res.status;
        var text = await res.text();
        out.response = text.slice(0, 300);
        console.log('[csf] HTTP', res.status, text.slice(0, 300));

        if (res.status === 401 || /sign in|accounts\.google/i.test(text)) {
          console.error('[csf] FAIL: the Apps Script deployment is asking for a Google login. ' +
            'Redeploy with "Who has access" set to Anyone, not "Anyone with Google account".');
        } else if (/unauthorised|unauthorized/i.test(text)) {
          console.error('[csf] FAIL: the script rejected the secret. ' +
            'SHARED_SECRET in the Apps Script must exactly match the shop metafield ' +
            'custom.sizing_capture_secret.');
        } else if (/"ok"\s*:\s*true/.test(text)) {
          console.log('[csf] PASS: the endpoint accepted a test row. Check the Submissions tab ' +
            'for a row with product "DIAGNOSTIC TEST".');
          out.ok = true;
        } else {
          console.warn('[csf] Unexpected response. Check the Errors tab in the sheet.');
        }
      } catch (err) {
        out.error = String(err);
        console.error('[csf] FAIL: the request could not be made at all:', err);
        console.error('[csf] Usually this is the deployment URL being wrong, or the deployment ' +
          'not being a Web app. Open the URL in a browser tab: it should print ' +
          '{"ok":true,"service":"barnfield-sizing-capture"}');
      }
      return out;
    };

    /* The answered subset of state.inputs, keyed as size-algorithm.js expects.
     * Sent with every capture so a Sheet row is readable on its own. */
    function capturedInputs() {
      var out = {};
      Object.keys(state.inputs).forEach(function (k) {
        if (state.inputs[k] !== null && state.inputs[k] !== undefined && state.inputs[k] !== '') {
          out[k] = state.inputs[k];
        }
      });
      return out;
    }

    /* Dialog semantics. The panel covers the page and takes focus, so it has
     * to announce itself as a dialog and hold the tab ring; without this a
     * keyboard or screen reader user tabs straight out of it into the product
     * page behind, with no way of knowing they have left. */
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');

    /* Scrolling.
     *
     * The panel is taller than the viewport on several screens (the Hampden's
     * Size guide is about 1400px of content in a 714px panel) and would not
     * scroll: the wheel moved the page behind it instead. The cause is
     * lenis-sitewide.js, which runs Lenis across the whole site and takes over
     * wheel events at the document level, so a nested scroll container never
     * sees them. Lenis walks up from the event target looking for
     * [data-lenis-prevent] and leaves that subtree to the browser, which is
     * the supported way out. Belt and braces, since a stuck panel is a dead
     * end for the customer:
     *   - data-lenis-prevent, so wheel events inside the panel scroll it
     *   - lenis.stop() while open, so the page cannot move behind it
     *   - the page itself locked, which is what stops touch scroll chaining
     *   - overscroll-behavior: contain in the CSS, so reaching the end of the
     *     panel does not start scrolling the page
     */
    panel.setAttribute('data-lenis-prevent', '');
    panel.setAttribute('aria-label', config.panelTitle || ('Find your size — ' + (config.productTitle || '')));
    body.setAttribute('tabindex', '-1');

    var lastFocused = null;

    if (trigger) trigger.addEventListener('click', open);
    closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (panel.hidden) return;
      // Escape puts away the top layer only: the drawer first, then the panel.
      if (e.key === 'Escape') { if (state.drawer) closeDrawer(); else close(); return; }
      if (e.key === 'Tab') trapFocus(e);
    });
    backBtn.addEventListener('click', goBack);
    drawerClose.addEventListener('click', closeDrawer);

    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

    function trapFocus(e) {
      var items = Array.prototype.filter.call(
        (state.drawer ? drawer : panel).querySelectorAll(FOCUSABLE),
        function (node) { return node.offsetParent !== null || node === document.activeElement; }
      );
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // Exposed so a CTA button elsewhere on the page can drive this flow
    // (see hide_own_trigger in custom-size-flow.liquid).
    root.csfOpen = open;

    /* Back from a waitlist signup (see the waitlist block): Shopify has
     * returned the customer to this page, so show them the confirmation. */
    var WAITLIST_PENDING_KEY = 'csf_waitlist_pending';
    if (WAITLIST) {
      var pendingSignup = null;
      try { pendingSignup = sessionStorage.getItem(WAITLIST_PENDING_KEY); } catch (e) {}
      var cameBack = /[?&](csf_waitlist=joined|customer_posted=true)/.test(location.search);
      if (pendingSignup && cameBack) {
        try { sessionStorage.removeItem(WAITLIST_PENDING_KEY); } catch (e) {}
        // Drop the marker from the address bar so a refresh does not replay this.
        try {
          var cleanUrl = new URL(location.href);
          ['csf_waitlist', 'customer_posted'].forEach(function (k) { cleanUrl.searchParams.delete(k); });
          history.replaceState(null, '', cleanUrl.pathname + cleanUrl.search);
        } catch (e) {}
        setTimeout(function () {
          open();
          waitlistJoined();
        }, 0);
      }
    }
    root.csfClose = close;

    // Open/close are staged in two steps rather than a single class flip:
    // - opening: the panel slides in immediately, its content fades in a
    //   beat later (is-open, then is-content-visible)
    // - closing: content fades out first, THEN the panel slides away
    //   (is-content-visible removed, then is-open removed after a delay) -
    //   the reverse order, per the brief this shipped against.
    // Timings are duplicated in custom-size-flow.css (transition durations)
    // and here (setTimeout delays) since plain CSS can't express "wait for
    // one element's transition to fully finish before starting another's"
    // across two different elements/properties reliably.
    var OPEN_CONTENT_DELAY = 160;
    var CLOSE_CONTENT_FADE = 220;
    var CLOSE_SLIDE_DURATION = 460;
    var STEP_FADE_MS = 140;
    var openTimer = null;
    var closeTimer = null;
    var hideTimer = null;
    var stepFadeTimer = null;

    var scrollLock = { y: 0, active: false };

    /* The page lock must never change the page's scroll position.
     *
     * The first version pinned <body> with position:fixed and top:-scrollY,
     * the usual trick. It keeps the page visually still, but the browser then
     * reports window.pageYOffset as 0. header.liquid listens for scroll and
     * toggles body--scrolled at 60px on / 20px off, and that class is what gives
     * the menu its solid background and scrolled position. So opening the panel
     * told the header the customer had jumped to the top of the page: the menu
     * went transparent and dropped down, and anything else on the site keyed to
     * scroll position would have been fooled the same way.
     *
     * Now the page is held with overflow:hidden on <html> only, which blocks
     * scrolling without moving the scroll position, plus lenis.stop() for the
     * smooth-scroll layer. pageYOffset stays exactly where it was, so the header
     * never notices the panel. */
    function lockPage() {
      if (scrollLock.active) return;
      scrollLock.y = window.scrollY || window.pageYOffset || 0;
      scrollLock.active = true;
      /* How wide the page scrollbar is, measured before it goes. Locking the
       * page removes it, and the CSS gives the same width back as padding
       * (see html.csf-scroll-locked) so nothing behind the panel moves. Zero
       * on phones and on Macs with overlay scrollbars. */
      var de = document.documentElement;
      de.style.setProperty('--csf-scrollbar', Math.max(0, window.innerWidth - de.clientWidth) + 'px');
      // Lenis animates window scroll on its own rAF loop, so it has to be
      // told to stop as well as the page being locked.
      if (window.lenis && typeof window.lenis.stop === 'function') {
        try { window.lenis.stop(); } catch (e) { /* older build */ }
      }
      document.documentElement.classList.add('csf-scroll-locked');
    }

    function unlockPage() {
      if (!scrollLock.active) return;
      scrollLock.active = false;
      document.documentElement.classList.remove('csf-scroll-locked');
      document.documentElement.style.removeProperty('--csf-scrollbar');
      // Belt and braces only: nothing above should have moved the page, but if
      // anything did, put it back where the customer left it.
      if (Math.abs((window.scrollY || window.pageYOffset || 0) - scrollLock.y) > 1) {
        window.scrollTo(0, scrollLock.y);
      }
      if (window.lenis && typeof window.lenis.start === 'function') {
        try { window.lenis.start(); } catch (e) { /* older build */ }
      }
    }

    function open() {
      lastFocused = document.activeElement;
      state.step = 'mode';
      state.history = [];
      state.variantError = null;
      state.fromCompare = false;
      resetDrawer();
      capture.ensureSession();
      if (WAITLIST) armWaitlistForm();
      if (trigger) trigger.hidden = true;
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      clearTimeout(hideTimer);
      panel.classList.remove('is-content-visible');
      panel.hidden = false;
      lockPage();
      reserveBottomChrome();
      render();
      // Force layout so the browser registers the panel's off-screen
      // starting position before is-open is added - otherwise adding the
      // class in the same tick as removing [hidden] can get coalesced into
      // one paint with no visible slide.
      void panel.offsetHeight;
      panel.classList.add('is-open');
      openTimer = setTimeout(function () {
        panel.classList.add('is-content-visible');
      }, OPEN_CONTENT_DELAY);
    }

    function close() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      clearTimeout(hideTimer);
      // Released immediately rather than after the slide-out, so the page is
      // never left locked if a later timer is cleared by a fresh open().
      unlockPage();
      if (state.drawer) { drawerOpener = null; closeDrawer(); }
      panel.classList.remove('is-content-visible');
      closeTimer = setTimeout(function () {
        panel.classList.remove('is-open');
        hideTimer = setTimeout(function () {
          panel.hidden = true;
          if (trigger) trigger.hidden = false;
          // Return the caret to whatever opened the panel, so a keyboard user
          // resumes where they left off rather than at the top of the document.
          if (lastFocused && typeof lastFocused.focus === 'function') {
            try { lastFocused.focus(); } catch (e) { /* node may be gone */ }
          }
        }, CLOSE_SLIDE_DURATION);
      }, CLOSE_CONTENT_FADE);
    }

    function goTo(step) {
      state.history.push(state.step);
      state.step = step;
      // Each screen starts at its own top. Without this, moving from a long
      // screen to a short one leaves the panel scrolled past the new content.
      panel.scrollTop = 0;
      renderWithFade();
    }

    function goBack() {
      if (!state.history.length) return;
      state.step = state.history.pop();
      renderWithFade();
    }

    /* ---------------------------------------------------------- drawer */

    // Must match the .csf__drawer transition in custom-size-flow.css.
    var DRAWER_SLIDE_MS = 460;
    var drawerTimer = null;
    var drawerOpener = null;
    var lastDrawerView = null;

    /* guide: { tab, jump } - which half of the Size guide to open on, and
     * optionally the measurement to scroll to. Ignored by the other views. */
    function openDrawer(view, guide) {
      drawerOpener = document.activeElement;
      clearTimeout(drawerTimer);
      state.drawer = view;
      if (view === 'guide') {
        state.guideTab = (guide && guide.tab) || 'measure';
        state.guideJump = (guide && guide.jump) || null;
      }
      lastDrawerView = null;
      drawer.hidden = false;
      renderDrawer();
      // Same reason as in open(): let the off-screen position register first.
      void drawer.offsetHeight;
      drawer.classList.add('is-open');
      drawerClose.focus({ preventScroll: true });
    }

    // A change of screen inside the drawer (compare -> its result and back).
    function showDrawer(view) {
      state.drawer = view;
      renderDrawer();
    }

    /* Takes no arguments on purpose: it is used directly as a click handler,
     * and would otherwise be handed the event. */
    function closeDrawer() {
      if (!state.drawer) return;
      state.drawer = null;
      clearTimeout(drawerTimer);
      drawer.classList.remove('is-open');
      drawerTimer = setTimeout(function () {
        drawer.hidden = true;
        drawerBody.innerHTML = '';
      }, DRAWER_SLIDE_MS);
      // Focus goes back under the drawer straight away, to the button that
      // opened it if that step is still on screen.
      if (drawerOpener && document.contains(drawerOpener)) {
        try { drawerOpener.focus({ preventScroll: true }); } catch (e) { /* node may be gone */ }
      } else if (document.activeElement && drawer.contains(document.activeElement)) {
        body.focus({ preventScroll: true });
      }
      drawerOpener = null;
    }

    function resetDrawer() {
      clearTimeout(drawerTimer);
      state.drawer = null;
      drawerOpener = null;
      drawer.classList.remove('is-open');
      drawer.hidden = true;
      drawerBody.innerHTML = '';
    }

    function renderDrawer() {
      /* Same rule as render(): repainting the screen already showing (a unit
       * toggle, a size chip) must not move the customer or drop their focus. */
      var sameView = lastDrawerView === state.drawer;
      var keepScroll = sameView ? drawer.scrollTop : 0;
      var focusIndex = -1;
      if (sameView && document.activeElement && drawerBody.contains(document.activeElement)) {
        focusIndex = Array.prototype.indexOf.call(
          drawerBody.querySelectorAll(FOCUSABLE_IN_BODY), document.activeElement);
      }

      drawerBody.innerHTML = '';
      if (state.drawer === 'guide') renderGuide();
      else if (state.drawer === 'compare') renderCompare();
      else if (state.drawer === 'compareResult') renderCompareResult();
      drawer.setAttribute('aria-label', state.drawer === 'guide' ? 'Size guide' : 'Compare my size');
      lastDrawerView = state.drawer;

      drawer.scrollTop = keepScroll;
      if (focusIndex > -1) {
        var after = drawerBody.querySelectorAll(FOCUSABLE_IN_BODY);
        if (after[focusIndex]) after[focusIndex].focus({ preventScroll: true });
      } else if (!sameView && drawer.classList.contains('is-open')) {
        drawerClose.focus({ preventScroll: true });
      }
    }

    /* The How to measure entry that belongs to a question, if the flow has
     * one: the chest question to "Chest", collar to "Collar", waist to
     * "Waist". Matched on the start of the label so a flow can call it
     * "Collar size" without breaking the link. */
    var GUIDE_WORD_FOR_STEP = { chestRange: 'chest', collarIn: 'collar', waistIn: 'waist' };
    function guideLabelFor(step) {
      var word = GUIDE_WORD_FOR_STEP[step];
      if (!word) return null;
      var labels = Object.keys(MEASURE_GUIDE);
      for (var i = 0; i < labels.length; i++) {
        if (labels[i].toLowerCase().indexOf(word) === 0) return labels[i];
      }
      return null;
    }

    function canShowGuide() { return MEASURE_ROWS_READY() || CHEST_SIZES.length > 0; }
    function canShowCompare() { return MEASURE_ROWS_READY() && COMPARE_POINTS.length > 0; }

    /* The two buttons that open the drawer, offered on both I Know My Size
     * steps (size, then length) and on every Find My Size question, between the answer and the Back / Next row.
     * Each is offered only when the flow has the data behind it: a flow with no
     * published measurements would open an empty table and an unanswerable
     * comparison, so the button is left out rather than shown broken. */
    function appendHelpLinks() {
      var waitlistFront = WAITLIST && state.step === 'mode';
      if (!waitlistFront && state.step !== 'standard' && state.step !== 'standardBuild' &&
          CUSTOM_STEPS.indexOf(state.step) === -1) return;
      /* The Size guide opens on How to measure wherever it is opened from; the
       * chart is the second tab. On the three questions that ask for a tape
       * measurement the button says so and lands on that diagram. */
      var onQuestion = CUSTOM_STEPS.indexOf(state.step) !== -1;
      var jump = onQuestion ? guideLabelFor(state.step) : null;
      var links = [];
      if (canShowGuide()) {
        links.push([jump ? 'How to measure' : 'Size guide', 'guide',
          { tab: 'measure', jump: jump }]);
      }
      if (canShowCompare()) links.push(['Compare my size', 'compare']);
      if (!links.length) return;

      var wrap = el('div', 'csf__help' + (links.length === 1 ? ' csf__help--single' : ''));
      links.forEach(function (link) {
        var btn = el('button', 'csf__help-btn');
        btn.type = 'button';
        btn.appendChild(el('span', '', link[0]));
        var arrow = el('span', 'csf__help-arrow', '→');
        arrow.setAttribute('aria-hidden', 'true');
        btn.appendChild(arrow);
        btn.addEventListener('click', function () { openDrawer(link[1], link[2]); });
        wrap.appendChild(btn);
      });
      var ctaRow = body.querySelector('.csf__cta-row');
      // On the waitlist form: under the size buttons, above the error and Join.
      if (!ctaRow && waitlistFront) ctaRow = body.querySelector('.csf__error') || body.querySelector('.csf__next');
      if (ctaRow) body.insertBefore(wrap, ctaRow);
      else body.appendChild(wrap);
    }

    /* ---------------------------------------------------------- render */

    // Fades the body out, swaps its content while invisible, then fades it
    // back in - used only for real step/screen changes (goTo/goBack and the
    // three step-changing render() calls inside computeResult()), not for
    // in-place UI updates within the same step (unit toggles, choice
    // selections, numbers panel expand, review adjusters, email capture),
    // which call render() directly so they stay instant and don't feel janky.
    function renderWithFade() {
      clearTimeout(stepFadeTimer);
      body.classList.add('csf__body--transitioning');
      stepFadeTimer = setTimeout(function () {
        render();
        void body.offsetHeight;
        body.classList.remove('csf__body--transitioning');
      }, STEP_FADE_MS);
    }

    /* Which step the body currently holds, so render() can tell a genuine step
     * change from a repaint of the step already on screen. */
    var lastRenderedStep = null;
    var FOCUSABLE_IN_BODY = 'input, button, a[href], select, textarea';

    function render() {
      /* A repaint of the same step must not move the customer. Rebuilding the
       * body destroys the element they were interacting with, and the old
       * unconditional focus() of the first button then scrolled the panel back
       * to the top: clicking a size chip near the bottom of the Size guide threw
       * you back to the heading every time. */
      var sameStep = lastRenderedStep === state.step;
      var keepScroll = sameStep ? panel.scrollTop : 0;
      var focusIndex = -1;
      if (sameStep && document.activeElement && body.contains(document.activeElement)) {
        focusIndex = Array.prototype.indexOf.call(
          body.querySelectorAll(FOCUSABLE_IN_BODY), document.activeElement);
      }

      backBtn.hidden = state.history.length === 0;
      var idx = CUSTOM_STEPS.indexOf(state.step);
      progress.hidden = idx === -1;
      if (idx !== -1) {
        /* The design deliberately never reaches 100% until the result is
         * shown, so the bar does not read "done" before the customer has
         * submitted anything. That was expressed as a flat (idx+1)*12, which
         * only lands correctly on a flow with exactly eight questions: the
         * knitwear flow's five questions would stall the bar at 60%, and a
         * flow with nine would run it past 100%. Same intent, derived from
         * the actual step count. */
        var ceiling = 96;
        progressBar.style.width = Math.min(ceiling, (idx + 1) * (ceiling / CUSTOM_STEPS.length)) + '%';
      }

      body.innerHTML = '';
      var renderers = {
        mode: renderMode,
        standard: renderStandard,
        standardBuild: renderStandardBuild,
        heightCm: renderHeight,
        weightKg: renderWeight,
        shoeSizeEu: renderShoe,
        chestRange: renderChest,
        collarIn: renderCollar,
        waistIn: renderWaist,
        fit: renderFit,
        loading: renderLoading,
        review: renderReview,
        error: renderError,
        basket: renderBasket,
        book: renderBook,
        waitlist: renderWaitlist,
        waitlistDone: renderWaitlistDone
      };
      var renderer = renderers[state.step];
      if (!renderer) {
        /* A question configured in the admin with an inputKey this file has no
         * bespoke renderer for. Previously this warned and silently skipped the
         * step, so a merchant could add a question, watch it never appear, and
         * get no visible error - the config layer promised more than it could
         * deliver. Now the question's own `kind` drives a generic renderer, and
         * only a question with no usable kind is skipped. The eight bespoke
         * renderers above are kept because they carry unit conversion the
         * generic ones cannot infer (ft/in, lbs, UK/US shoe sizes). */
        var kind = q(state.step).kind;
        if (CUSTOM_STEPS.indexOf(state.step) !== -1 && kind) {
          genericStep(state.step, kind);
        } else {
          console.warn('[csf] no renderer and no usable kind for step:', state.step);
          advance(state.step);
          return;
        }
      } else {
        renderer();
      }
      appendHelpLinks();

      lastRenderedStep = state.step;

      if (sameStep) {
        panel.scrollTop = keepScroll;
        /* Return focus to the control in the same position as the one that held
         * it, which after a repaint is the same control: the chip they clicked,
         * the unit they toggled. Without this a keyboard user is dropped back to
         * the top of the tab order on every selection. */
        if (focusIndex > -1) {
          var after = body.querySelectorAll(FOCUSABLE_IN_BODY);
          if (after[focusIndex]) after[focusIndex].focus({ preventScroll: true });
        }
      } else {
        /* The waitlist form opens with an intro to read first: focusing its
         * name field would raise a phone's keyboard over that intro. */
        var firstInput = (WAITLIST && state.step === 'mode')
          ? panel.querySelector('[data-csf-close]')
          : body.querySelector('input, button');
        if (firstInput) firstInput.focus({ preventScroll: true });
      }
    }

    /* ------------------------------------------------------- mode select */

    function renderMode() {
      // Per the design handoff, the eyebrow/title/intro copy only appears
      // on this first screen - every other step just gets its own
      // question heading under the head-row (back/progress/close). So
      // this is rendered here in the body, not as persistent chrome.
      body.appendChild(el('div', 'csf__eyebrow', config.panelEyebrow || ('Fit & Size — ' + config.productTitle))); // liquid already supplies the full "Fit & Size — <title>" string; this fallback only matters if bookingUrl/panelEyebrow config is ever hand-edited
      body.appendChild(el('h1', 'csf__panel-title', config.panelTitle || ('Find your ' + config.productTitle)));
      if (config.panelSubtext) appendRichText(body, config.panelSubtext, 'csf__hint');

      /* Waitlist mode asks for nothing but a name, an email and a size, so
       * the form sits here under the intro and the sizing paths are not
       * offered: a questionnaire in front of a signup is a barrier, not a
       * service. The Size guide stays one tap away (see appendHelpLinks). */
      if (WAITLIST) {
        appendWaitlistForm(true);
        return;
      }

      var rows = buildModeRows();
      var list = el('div', 'csf__mode-list');
      rows.forEach(function (row, i) {
        var isFirst = i === 0;
        var isLast = i === rows.length - 1;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'csf__mode-row' + (isFirst ? ' csf__mode-row--first' : '') + (isLast ? ' csf__mode-row--last' : '');
        var left = el('span', 'csf__mode-left');
        left.appendChild(el('span', 'csf__mode-num', pad2(i + 1)));
        var textWrap = el('span', 'csf__mode-text');
        textWrap.appendChild(el('span', 'csf__mode-title', row.title));
        textWrap.appendChild(el('span', 'csf__mode-desc', row.desc));
        left.appendChild(textWrap);
        btn.appendChild(left);
        btn.appendChild(el('span', 'csf__mode-arrow', '→'));
        btn.addEventListener('click', row.onClick);
        list.appendChild(btn);
      });
      body.appendChild(list);
    }

    function buildModeRows() {
      var rows = [];
      var saved = loadJSON(LS_SIZES) || [];

      if (saved.length) {
        rows.push({
          id: 'saved',
          title: 'Use a Saved Size',
          desc: saved[saved.length - 1].name || 'Your size and fit',
          onClick: function () {
            var s = saved[saved.length - 1];
            state.inputs = Object.assign({}, state.inputs, s.inputs);
            state.result = Object.assign({ recommendedBuild: config.buildValues && config.buildValues[0] }, s.result);
            /* Entries saved before this field existed have no `chosen`, so fall
             * back to the recommendation rather than blanking the screen. */
            state.chosen = s.chosen || {
              size: state.result.recommendedSize,
              build: state.result.recommendedBuild,
              chestCm: state.result.measurements ? state.result.measurements.chestCm : null
            };
            state.sizeName = s.name;
            goTo('review');
          }
        });
      }

      if (config.hasSizeOption) {
        rows.push({
          id: 'standard',
          title: 'I Know My Size',
          desc: 'Choose directly from ' + ladderRangeLabel(standardSizeValues()) +
            (config.hasBuildOption ? ' in ' + listWithOr(config.buildValues) + '.' : '.'),
          onClick: function () { goTo('standard'); }
        });
      }

      rows.push({
        id: 'find',
        title: 'Find My Size',
        desc: 'Answer a few questions to identify your ideal size' + (config.hasBuildOption ? ' and fit.' : '.'),
        onClick: function () { goTo(CUSTOM_STEPS[0]); }
      });

      if (config.bookingEmbedUrl || config.bookingUrl) {
        rows.push({
          id: 'book',
          title: 'Book a Call',
          desc: 'Speak with a trained consultant to identify your size' + (config.hasBuildOption ? ' and fit.' : '.'),
          /* The calendar opens here, in the panel. Only a flow with a booking
           * link but no embeddable calendar still opens a new tab. */
          onClick: function () {
            if (config.bookingEmbedUrl) goTo('book');
            else window.open(config.bookingUrl, '_blank', 'noopener');
          }
        });
      }

      /* The flow's "Panel options shown" list, when set, both filters and
       * orders the rows. A named path with no data behind it still does not
       * appear, so this can only ever remove or reorder what the flow could
       * already offer, never fabricate a broken screen. 'compare' and 'guide'
       * in that list are now ignored: they are no longer rows, and open from
       * inside the other paths instead (see appendHelpLinks).
       *
       * 'saved' is exempt: it is a returning visitor's own record, not a path
       * the merchant configures, and silently dropping it would look like
       * losing their data. */
      var allowed = config.enabledPaths;
      if (allowed && allowed.length) {
        var wanted = allowed.map(function (s) { return String(s).trim().toLowerCase(); });
        var byId = {};
        rows.forEach(function (row) { byId[row.id] = row; });
        var ordered = [];
        if (byId.saved) ordered.push(byId.saved);
        wanted.forEach(function (id) {
          if (id !== 'saved' && byId[id] && ordered.indexOf(byId[id]) === -1) ordered.push(byId[id]);
        });
        if (ordered.length) return ordered;
        console.warn('[csf] enabled_paths matched no available rows; showing all.');
      }

      return rows;
    }

    function MEASURE_ROWS_READY() { return MEASUREMENT_ROWS.length > 0; }

    /* ------------------------------------------------------ book a call */

    /* Calendly's inline calendar, in the panel, in place of a link out to
     * calendly.com. Their widget script is loaded the first time this screen
     * is opened rather than with the page: it is third-party code most
     * visitors to a product page never need.
     *
     * If the script cannot load (blocked, offline) the customer gets the old
     * link instead of an empty box. */
    var CALENDLY_SRC = 'https://assets.calendly.com/assets/external/widget.js';
    var calendlyLoading = null;
    function loadCalendly() {
      if (window.Calendly && typeof window.Calendly.initInlineWidget === 'function') return Promise.resolve();
      if (calendlyLoading) return calendlyLoading;
      calendlyLoading = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = CALENDLY_SRC;
        script.async = true;
        script.onload = function () { resolve(); };
        script.onerror = function () { calendlyLoading = null; reject(new Error('Calendly script failed to load')); };
        document.head.appendChild(script);
      });
      return calendlyLoading;
    }

    /* The same height message, handled here as well, in case a cached or
     * older widget.js ignores the resize option above. */
    window.addEventListener('message', function (e) {
      if (!/^https:\/\/([a-z0-9-]+\.)?calendly\.com$/.test(e.origin)) return;
      var data = e.data;
      if (!data || data.event !== 'calendly.page_height' || !data.payload) return;
      var px = parseFloat(data.payload.height);
      var holder = body.querySelector('.csf__calendly');
      if (holder && isFinite(px) && px > 0) holder.style.height = Math.ceil(px) + 'px';
    });

    /* Book a Call is offered in two places: as a row on the first screen and
     * again under the recommendation. Both honour the flow's "Panel options
     * shown" list, so a flow that leaves the call out leaves it out of both. */
    function canBook() {
      if (!config.bookingEmbedUrl && !config.bookingUrl) return false;
      var allowed = config.enabledPaths;
      if (!allowed || !allowed.length) return true;
      return allowed.some(function (s) { return String(s).trim().toLowerCase() === 'book'; });
    }

    function openBooking() {
      if (config.bookingEmbedUrl) goTo('book');
      else window.open(config.bookingUrl, '_blank', 'noopener');
    }

    /* What the customer has already told us, as one line for whoever takes
     * the call. Without it the calendar opened blank: someone could answer
     * all eight questions, book, and then be asked every one of them again.
     * It goes into the event's own "anything that will help prepare" field,
     * where the customer can read it, change it or delete it before booking.
     * Whatever was answered is included, so a customer who gave up half way
     * and booked instead still arrives with that half. */
    /* Sizes are spelt out in the booking note: it is read by a person, in an
     * email, away from the size buttons that give "L" its meaning. */
    var SIZE_WORDS = {
      'XS': 'X-Small', 'S': 'Small', 'M': 'Medium', 'L': 'Large',
      'XL': 'X-Large', '2XL': '2XL', 'XXL': '2XL'
    };
    function sizeWords(size, build, separator) {
      var word = SIZE_WORDS[String(size).toUpperCase()] || size;
      return word + (config.hasBuildOption && build ? (separator || ' ') + build : '');
    }

    function bookingNotes() {
      var i = state.inputs;
      var answers = [];
      if (i.heightCm != null) answers.push('height ' + heightLabel());
      if (i.weightKg != null) answers.push('weight ' + weightLabel());
      if (i.shoeSizeEu != null) answers.push('shoe EU ' + i.shoeSizeEu);
      if (i.chestRange) answers.push('chest ' + i.chestRange + '"');
      if (i.collarIn != null) answers.push('collar ' + i.collarIn + '"');
      if (i.waistIn != null) answers.push('waist ' + i.waistIn + '"');
      if (i.fit) answers.push(fitLabel().toLowerCase() + ' fit');
      if (!answers.length) return '';

      var parts = ['Find My Size answers for the ' + config.productTitle + ': ' + answers.join(', ') + '.'];
      var r = state.result;
      var chosen = state.chosen;
      if (r && r.recommendedSize) {
        parts.push('Recommended ' + sizeWords(r.recommendedSize, r.recommendedBuild) + '.');
        if (chosen && chosen.size &&
            (chosen.size !== r.recommendedSize || chosen.build !== r.recommendedBuild)) {
          parts.push('Changed to ' + sizeWords(chosen.size, chosen.build) + '.');
        }
      }
      return parts.join(' ');
    }

    function renderBook() {
      var notes = bookingNotes();
      body.appendChild(el('h1', 'csf__title', 'Book a call'));
      var intro = [
        'A 30-minute video call to go through your measurements together and settle on the right size before you order.',
        'Have a tape measure to hand and a similar garment that fits you well. Comparing against something you already own is the quickest way to find the right fit.',
        'There\u2019s no obligation to order. If you\u2019d simply like to ask a question, this is the place for that too.'
      ];
      if (notes) intro.push('Your answers are added to the booking, so we have them to hand.');
      intro.forEach(function (text, n) {
        body.appendChild(el('p', 'csf__hint' + (n < intro.length - 1 ? ' csf__hint--stacked' : ''), text));
      });

      var holder = el('div', 'csf__calendly');
      var loading = el('div', 'csf__loading');
      loading.appendChild(el('div', 'csf__spinner', ''));
      holder.appendChild(loading);
      body.appendChild(holder);

      loadCalendly().then(function () {
        // The customer may have gone Back while the script was loading.
        if (!document.contains(holder)) return;
        holder.innerHTML = '';
        /* resize: Calendly reports the height of whichever of its screens is
         * showing and the holder is sized to match, so the calendar never
         * scrolls inside its own frame. At a fixed 700px the details form,
         * which is taller than the date picker, grew a second scrollbar
         * inside the panel's own. The panel does the scrolling instead. */
        var widget = { url: config.bookingEmbedUrl, parentElement: holder, resize: true };
        // a1 is the event's first invitee question, the free-text notes field.
        if (notes) widget.prefill = { customAnswers: { a1: notes } };
        window.Calendly.initInlineWidget(widget);
      }).catch(function (err) {
        console.warn('[csf] Calendly embed unavailable, offering the link:', err);
        if (!document.contains(holder)) return;
        holder.innerHTML = '';
        holder.classList.add('csf__calendly--fallback');
        var link = document.createElement('a');
        link.className = 'csf__calendly-link';
        link.href = config.bookingUrl || config.bookingEmbedUrl;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = 'Open the booking calendar';
        holder.appendChild(el('p', 'csf__hint', 'The calendar could not be loaded here.'));
        holder.appendChild(link);
      });
    }

    /* ------------------------------------------------- compare my size */

    /* The customer measures a garment they own and like the fit of, and we
     * say which of ours is closest and by how much on each point.
     *
     * Why this path exists alongside Find My Size: Find My Size predicts a
     * body from height and weight and then adds ease. This one skips the
     * prediction entirely and compares like with like. When someone has a coat
     * they already know fits, that is much better evidence than a regression
     * trained on US Army recruits.
     *
     * Its weakness is measurement technique, which is why every point carries
     * its own instruction and why the result shows the gap on each point
     * rather than only a size. A customer who can see that their coat is 3 cm
     * wider in the chest than our L can make their own call; one handed a bare
     * "we recommend L" cannot. */
    function renderCompare() {
      drawerBody.appendChild(el('h1', 'csf__title', 'Compare my size'));
      drawerBody.appendChild(el('p', 'csf__hint',
        'Lay a garment you already wear well on a flat surface, buttoned or zipped and smoothed out. ' +
        'Measure it flat, in a straight line, not around the body. Fill in what you can; chest alone is enough to get a useful answer.'));

      var unit = state.compareUnit || 'cm';
      var seg = el('div', 'csf__seg');
      ['cm', 'in'].forEach(function (u) {
        var b = el('button', u === unit ? 'is-on' : '', u === 'cm' ? 'CM' : 'Inches');
        b.type = 'button';
        b.addEventListener('click', function () { state.compareUnit = u; renderDrawer(); });
        seg.appendChild(b);
      });
      drawerBody.appendChild(seg);

      var inputs = {};
      var fields = el('div', 'csf__compare-fields');

      COMPARE_POINTS.forEach(function (label) {
        var field = el('div', 'csf__compare-field');
        field.appendChild(el('span', 'csf__compare-label', label));
        if (COMPARE_GUIDE[label]) {
          field.appendChild(el('span', 'csf__compare-how', COMPARE_GUIDE[label].text));
        }
        var row = el('div', 'csf__num-field');
        var inputWrap = el('span', 'csf__num-input-wrap');
        var input = document.createElement('input');
        input.type = 'number';
        input.inputMode = 'decimal';
        input.step = 'any';
        input.className = 'csf__num-input';
        input.setAttribute('aria-label', label + ' in ' + (unit === 'cm' ? 'centimetres' : 'inches'));
        var storedCm = state.compare[label];
        input.value = storedCm == null ? '' : trimNum(unit === 'cm' ? storedCm : storedCm / 2.54);
        inputs[label] = input;
        inputWrap.appendChild(input);
        row.appendChild(inputWrap);
        row.appendChild(el('span', 'csf__unit-label', unit === 'cm' ? 'cm' : 'in'));
        field.appendChild(row);
        fields.appendChild(field);
      });
      drawerBody.appendChild(fields);

      var errEl = el('p', 'csf__error', '');
      errEl.setAttribute('role', 'alert');
      errEl.hidden = true;
      drawerBody.appendChild(errEl);

      var ctaRow = el('div', 'csf__cta-row');
      var back = el('button', 'csf__cta-back', 'Close');
      back.type = 'button';
      back.addEventListener('click', closeDrawer);
      var next = el('button', 'csf__next', 'Compare');
      next.type = 'button';
      next.addEventListener('click', runCompare);
      ctaRow.appendChild(back);
      ctaRow.appendChild(next);
      drawerBody.appendChild(ctaRow);

      Object.keys(inputs).forEach(function (label) {
        inputs[label].addEventListener('keydown', function (e) {
          if (e.key === 'Enter') runCompare();
        });
      });

      function runCompare() {
        var entered = {};
        var count = 0;
        var bad = null;

        COMPARE_POINTS.forEach(function (label) {
          var raw = inputs[label].value.trim();
          if (raw === '') return;
          var n = parseFloat(raw);
          if (!isFinite(n) || n <= 0) { bad = bad || label; return; }
          var cm = unit === 'cm' ? n : n * 2.54;
          // A flat garment point outside this range is a typo or a body
          // measurement taken around the chest rather than across it.
          if (cm < 15 || cm > 160) { bad = bad || label; return; }
          entered[label] = Math.round(cm * 10) / 10;
          count++;
        });

        if (bad) {
          errEl.textContent = 'Check the ' + bad.toLowerCase() + ' measurement. Measure the garment flat, in ' +
            (unit === 'cm' ? 'centimetres' : 'inches') + ', across rather than around.';
          errEl.hidden = false;
          return;
        }
        if (!count) {
          errEl.textContent = 'Enter at least one measurement. Chest is the most useful.';
          errEl.hidden = false;
          return;
        }

        state.compare = entered;
        state.compareResult = scoreCompare(entered);
        if (!state.compareResult) {
          errEl.textContent = 'We could not compare those. Please try again.';
          errEl.hidden = false;
          return;
        }
        capture.send('result_reached', {
          path: 'compare',
          compare: { points: entered, closest: state.compareResult.size },
          result: { recommendedSize: state.compareResult.size }
        });
        showDrawer('compareResult');
      }
    }

    /* Weighted mean absolute difference across the points the customer filled
     * in, lowest wins. Deliberately not a sum: a customer who fills in four
     * points would otherwise be scored on a different scale from one who
     * filled in one, and the runner-up gap would read as bigger than it is. */
    function scoreCompare(entered) {
      var labels = Object.keys(entered);
      if (!labels.length || !SIZE_LADDER.length) return null;

      var scored = SIZE_LADDER.map(function (size) {
        var weighted = 0;
        var weight = 0;
        var rows = [];
        labels.forEach(function (label) {
          var row = MEASUREMENT_ROWS.find(function (r) { return r.label === label; });
          var ours = row && row.values[size];
          if (ours == null) return;
          var w = compareWeight(label);
          weighted += Math.abs(ours - entered[label]) * w;
          weight += w;
          rows.push({ label: label, theirsCm: entered[label], oursCm: ours, deltaCm: Math.round((ours - entered[label]) * 10) / 10 });
        });
        return { size: size, score: weight ? weighted / weight : Infinity, rows: rows };
      }).filter(function (s) { return isFinite(s.score); });

      if (!scored.length) return null;
      scored.sort(function (a, b) { return a.score - b.score; });
      return scored[0];
    }

    function renderCompareResult() {
      var r = state.compareResult;
      drawerBody.appendChild(el('h1', 'csf__title', 'Closest to your garment'));

      var recBox = el('div', 'csf__rec-box');
      recBox.appendChild(el('div', 'csf__rec-headline', 'Our ' + r.size + ' is the closest match'));
      recBox.appendChild(el('div', 'csf__rec-detail',
        'Compared across ' + r.rows.length + ' ' + (r.rows.length === 1 ? 'point' : 'points') + ' of measurement.'));
      drawerBody.appendChild(recBox);

      var unit = state.compareUnit || 'cm';
      var seg = el('div', 'csf__seg');
      ['cm', 'in'].forEach(function (u) {
        var b = el('button', u === unit ? 'is-on' : '', u === 'cm' ? 'CM' : 'Inches');
        b.type = 'button';
        b.addEventListener('click', function () { state.compareUnit = u; renderDrawer(); });
        seg.appendChild(b);
      });
      drawerBody.appendChild(seg);

      /* Four columns fit, but only once the unit stops repeating in every
       * cell. Stating it once in the header buys back roughly a third of the
       * width and stops "56.5 cm" wrapping on a phone. */
      var u = unit === 'in' ? 'in' : 'cm';
      drawerBody.appendChild(buildTable(
        ['', 'Yours (' + u + ')', 'Our ' + r.size + ' (' + u + ')', 'Diff'],
        r.rows.map(function (row) {
          var d = row.deltaCm;
          var sign = d > 0 ? '+' : '';
          var conv = function (cm) {
            return unit === 'in'
              ? trimNum(Math.round((cm / 2.54) * 10) / 10)
              : trimNum(Math.round(cm * 100) / 100);
          };
          return [
            row.label,
            conv(row.theirsCm),
            conv(row.oursCm),
            Math.abs(d) < 0.05 ? 'same' : sign + conv(d)
          ];
        }),
        'csf__table--compare'
      ));

      /* Sign convention stated plainly. "+2 cm" is meaningless until you know
       * which garment it refers to, and getting it backwards would send
       * somebody a size the wrong way. */
      drawerBody.appendChild(el('p', 'csf__hint',
        'A plus figure means ours is larger than the garment you measured. Our measurements are the finished garment, measured flat.'));

      drawerBody.appendChild(el('div', 'csf__eyebrow-label', 'Compare against another size'));
      var adjustGrid = el('div', 'csf__adjust-grid');
      var idx = SIZE_LADDER.indexOf(r.size);
      adjustGrid.appendChild(cmpBtn('− Size down', -1, idx <= 0));
      adjustGrid.appendChild(cmpBtn('+ Size up', 1, idx >= SIZE_LADDER.length - 1 || idx === -1));
      drawerBody.appendChild(adjustGrid);

      if (state.variantError) {
        var warn = el('p', 'csf__error', state.variantError);
        warn.setAttribute('role', 'alert');
        drawerBody.appendChild(warn);
      }

      var ctaRow = el('div', 'csf__cta-row');
      var back = el('button', 'csf__cta-back', 'Edit measurements');
      back.type = 'button';
      back.addEventListener('click', function () { showDrawer('compare'); });
      var add = el('button', 'csf__next', config.hasBuildOption ? 'Choose length' : ADD_LABEL);
      add.type = 'button';
      add.addEventListener('click', addFromCompare);
      ctaRow.appendChild(back);
      ctaRow.appendChild(add);
      drawerBody.appendChild(ctaRow);

      function cmpBtn(label, delta, disabled) {
        var b = el('button', 'csf__adjust-btn', label);
        b.type = 'button';
        if (disabled) {
          b.disabled = true;
        } else {
          b.addEventListener('click', function () {
            var next = SIZE_LADDER[SIZE_LADDER.indexOf(r.size) + delta];
            if (!next) return;
            state.compareResult = rescoreAt(next);
            capture.sendAdjustDebounced({
              path: 'compare',
              compare: { points: state.compare, closest: next },
              chosenSize: next
            });
            renderDrawer();
          });
        }
        return b;
      }

      function rescoreAt(size) {
        var rows = [];
        Object.keys(state.compare).forEach(function (label) {
          var mrow = MEASUREMENT_ROWS.find(function (m) { return m.label === label; });
          var ours = mrow && mrow.values[size];
          if (ours == null) return;
          rows.push({
            label: label,
            theirsCm: state.compare[label],
            oursCm: ours,
            deltaCm: Math.round((ours - state.compare[label]) * 10) / 10
          });
        });
        return { size: size, score: 0, rows: rows };
      }

      /* The coat is cut in three lengths and nothing the customer measures on
       * a garment they own tells us which one to pick - the published table
       * has one length figure per size, not per build. So we ask, rather than
       * guessing on a made-to-order coat. Reuses the Standard path's build
       * step, which already disables combinations with no variant. */
      function addFromCompare() {
        state.inputs.standardSizeValue = r.size;
        if (config.hasBuildOption) {
          state.variantError = null;
          state.fromCompare = true;
          closeDrawer();
          // Opened from the length step itself: it is already underneath, and
          // only needs repainting for the size the comparison landed on.
          if (state.step === 'standardBuild') renderWithFade();
          else goTo('standardBuild');
          return;
        }
        var variant = findVariant(r.size, null);
        if (!sellable(variant)) {
          state.variantError = r.size + ' is not available at the moment.';
          renderDrawer();
          return;
        }
        state.variantError = null;
        capture.send(ADD_STATUS, {
          path: 'compare',
          compare: { points: state.compare, closest: r.size },
          chosenSize: r.size,
          variantId: variant.id
        });
        var props = { 'Size type': 'Compared', 'Size': r.size };
        r.rows.forEach(function (row) {
          props['Their ' + row.label.toLowerCase() + ' (cm)'] = String(row.theirsCm);
        });
        addToCart(variant.id, props);
      }
    }

    /* ------------------------------------------------------ size guide */

    function renderGuide() {
      drawerBody.appendChild(el('h1', 'csf__title', 'Size guide'));

      /* Two tabs, because the guide answers two questions. The chart is for
       * "which size am I?"; How to measure is for "how do I get this number?".
       * Run together, the second sat under two tables its reader did not
       * need. A flow with only one of the two has no tabs and shows what it
       * has, as before. */
      var guideKeys = Object.keys(MEASURE_GUIDE);
      var hasChart = CHEST_SIZES.length > 0 || MEASUREMENT_ROWS.length > 0;
      var hasMeasure = guideKeys.length > 0;
      var tabbed = hasChart && hasMeasure;
      var tab = state.guideTab === 'chart' ? 'chart' : 'measure';
      var showChart = !tabbed || tab === 'chart';
      var showMeasure = !tabbed || tab === 'measure';

      if (tabbed) {
        var tabs = el('div', 'csf__tabs');
        tabs.setAttribute('role', 'tablist');
        [['measure', 'How to measure'], ['chart', 'Size chart']].forEach(function (t) {
          var on = t[0] === tab;
          var tb = el('button', 'csf__tab' + (on ? ' is-on' : ''), t[1]);
          tb.type = 'button';
          tb.setAttribute('role', 'tab');
          tb.setAttribute('aria-selected', on ? 'true' : 'false');
          tb.addEventListener('click', function () {
            if (state.guideTab === t[0]) return;
            state.guideTab = t[0];
            state.guideJump = null;
            renderDrawer();
            // The other tab is a different screen: start it at its top.
            drawer.scrollTop = 0;
          });
          tabs.appendChild(tb);
        });
        drawerBody.appendChild(tabs);
      }

      if (showChart && config.fitNote) drawerBody.appendChild(el('p', 'csf__hint', config.fitNote));

      var unit = state.numbersUnit || 'cm';
      if (showChart) {
        var seg = el('div', 'csf__seg');
        ['cm', 'in'].forEach(function (u) {
          var b = el('button', u === unit ? 'is-on' : '', u === 'cm' ? 'CM' : 'Inches');
          b.type = 'button';
          b.addEventListener('click', function () { state.numbersUnit = u; renderDrawer(); });
          seg.appendChild(b);
        });
        drawerBody.appendChild(seg);
      }

      if (showChart && CHEST_SIZES.length) {
        drawerBody.appendChild(el('div', 'csf__eyebrow-label', 'Find your size'));
        drawerBody.appendChild(buildTable(
          ['Size', 'Your chest'],
          CHEST_SIZES.map(function (pair) {
            var range = String(pair[0]);
            var label = unit === 'in'
              ? range + '"'
              : range.split('-').map(function (n) { return Math.round(Number(n) * 2.54); }).join('–') + ' cm';
            return [pair[1], label];
          }),
          'csf__table--conversion'
        ));
        drawerBody.appendChild(el('p', 'csf__hint', 'Your own chest, measured around the fullest part.'));
      }

      if (showChart && MEASUREMENT_ROWS.length) {
        /* One size at a time, chosen with the same size buttons used everywhere
         * else in the panel, rather than all six as columns. The customer is
         * not comparing sizes here; they have a size in mind and want its
         * numbers. Picking one and reading a list beats scrolling a grid
         * sideways to find the column they care about. */
        drawerBody.appendChild(el('div', 'csf__eyebrow-label', 'Our garment measurements'));

        /* Preselect whatever the panel already knows about them: the size they
         * were recommended, or the one they picked on the standard path. With
         * nothing known, the middle of the ladder, which is a visibly
         * changeable starting point rather than a suggestion. */
        var known = (state.chosen && state.chosen.size) || state.inputs.standardSizeValue;
        var sel = (state.guideSize && SIZE_LADDER.indexOf(state.guideSize) !== -1)
          ? state.guideSize
          : ((known && SIZE_LADDER.indexOf(known) !== -1)
              ? known
              : SIZE_LADDER[Math.floor((SIZE_LADDER.length - 1) / 2)]);
        state.guideSize = sel;

        var sizeRow = el('div', 'csf__size-grid csf__size-grid--guide');
        sizeRow.setAttribute('role', 'group');
        sizeRow.setAttribute('aria-label', 'Choose a size to see its measurements');
        SIZE_LADDER.forEach(function (size) {
          var btn = el('button', 'csf__size-btn' + (size === sel ? ' is-selected' : ''), size);
          btn.type = 'button';
          btn.setAttribute('aria-pressed', size === sel ? 'true' : 'false');
          btn.addEventListener('click', function () {
            state.guideSize = size;
            renderDrawer();
          });
          sizeRow.appendChild(btn);
        });
        drawerBody.appendChild(sizeRow);

        var rows = el('div', 'csf__numbers-rows');
        var shownAny = false;
        MEASUREMENT_ROWS.forEach(function (row) {
          if (row.values[sel] == null) return;
          shownAny = true;
          var line = el('div', 'csf__numbers-row');
          line.appendChild(el('span', '', row.label));
          line.appendChild(el('span', '', fmtLen(row.values[sel], unit)));
          rows.appendChild(line);
        });
        drawerBody.appendChild(rows);

        drawerBody.appendChild(el('p', 'csf__hint', shownAny
          ? 'Size ' + sel + ', finished garment, measured flat. Not body measurements.'
          : 'No published measurements for size ' + sel + '.'));
      }

      if (showMeasure && hasMeasure) {
        // With tabs the tab is the heading; without them this still is.
        if (!tabbed) drawerBody.appendChild(el('div', 'csf__eyebrow-label', 'How to measure yourself'));
        var jumpTo = null;
        var list = el('div', 'csf__guide-list');
        guideKeys.forEach(function (label) {
          var entry = MEASURE_GUIDE[label];
          var item = el('div', 'csf__guide-item');
          if (label === state.guideJump) jumpTo = item;
          item.appendChild(el('span', 'csf__compare-label', label));
          if (entry.image) {
            /* The same diagrams as the How to measure page. A measurement
             * instruction is one of the few things genuinely easier to show
             * than to write, and shoulder width in particular is close to
             * unteachable in prose alone. */
            var figure = el('div', 'csf__guide-figure');
            var img = document.createElement('img');
            img.src = entry.image;
            img.alt = 'How to measure ' + label.toLowerCase();
            /* Deliberately NOT loading="lazy". The panel is position:fixed with
             * its own internal scroll, and the lazy-load intersection check
             * never fired inside it: the images stayed at naturalWidth 0, which
             * with height:auto collapsed them to nothing, which kept them out
             * of view, which kept them from loading. Three diagrams on a screen
             * the customer deliberately opened do not need deferring, and
             * width=600 on the CDN URL already keeps them small. */
            img.decoding = 'async';
            figure.appendChild(img);
            item.appendChild(figure);
          }
          item.appendChild(el('span', 'csf__compare-how', entry.text));
          list.appendChild(item);
        });
        drawerBody.appendChild(list);

        /* Opened from the chest, collar or waist question: land on that
         * diagram. Done once, on a timer so it runs after renderDrawer has
         * put the scroll position back, and cleared so that repainting the
         * tab does not drag the customer back here. The diagrams reserve
         * their height in the CSS, so the position holds as they load. */
        if (jumpTo) {
          state.guideJump = null;
          setTimeout(function () {
            if (!document.contains(jumpTo)) return;
            drawer.scrollTop += jumpTo.getBoundingClientRect().top - drawer.getBoundingClientRect().top - 24;
          }, 0);
        }
      }

      var ctaRow = el('div', 'csf__cta-row');
      var back = el('button', 'csf__cta-back', 'Close');
      back.type = 'button';
      back.addEventListener('click', closeDrawer);
      ctaRow.appendChild(back);
      if (config.hasSizeOption) {
        var pick = el('button', 'csf__next', 'Select size & buy');
        pick.type = 'button';
        pick.addEventListener('click', function () {
          // Opened from I Know My Size, the size buttons are already underneath.
          closeDrawer();
          // From the length step, the size buttons are one step back.
          if (state.step === 'standardBuild' && state.history[state.history.length - 1] === 'standard') goBack();
          else if (state.step !== 'standard') goTo('standard');
        });
        ctaRow.appendChild(pick);
      }
      drawerBody.appendChild(ctaRow);
    }

    /* -------------------------------------------------- table + length */

    /* Wrapped in an overflow-x container: the coat's measurement table is
     * seven columns wide and the panel is a phone-width sidebar, so the table
     * has to scroll inside its own box rather than push the panel sideways. */
    function buildTable(headers, rows, extraClass) {
      var scroller = el('div', 'csf__table-scroll');
      var table = document.createElement('table');
      table.className = 'csf__table' + (extraClass ? ' ' + extraClass : '');

      var thead = document.createElement('thead');
      var headRow = document.createElement('tr');
      headers.forEach(function (h, i) {
        var th = document.createElement('th');
        th.textContent = h;
        th.scope = 'col';
        if (i === 0) th.className = 'csf__table-rowhead';
        headRow.appendChild(th);
      });
      thead.appendChild(headRow);
      table.appendChild(thead);

      var tbody = document.createElement('tbody');
      rows.forEach(function (cells) {
        var tr = document.createElement('tr');
        cells.forEach(function (cell, i) {
          var node = document.createElement(i === 0 ? 'th' : 'td');
          if (i === 0) {
            node.scope = 'row';
            node.className = 'csf__table-rowhead';
          }
          node.textContent = cell;
          tr.appendChild(node);
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);

      scroller.appendChild(table);
      return scroller;
    }

    /* Centimetres keep two decimals, matching fmtSpec() above and for the same
     * reason: these are published points of measure, not estimates. Rounding to
     * one decimal printed the specified 26.75 cm armhole as 26.8, which is the
     * same class of error as the round1() half-centimetre snapping that was
     * removed from this file earlier. Inches are derived, so one decimal there.
     * trimNum drops trailing zeros, so 56 cm does not print as 56.00 cm. */
    function fmtLen(cm, unit) {
      if (unit === 'in') return trimNum(Math.round((cm / 2.54) * 10) / 10) + '"';
      return trimNum(Math.round(cm * 100) / 100) + ' cm';
    }

    /* Generic renderers for admin-configured questions with no bespoke
     * handler. No unit conversion: the value is stored on state.inputs exactly
     * as entered, under the question's own inputKey, which is the contract
     * size-algorithm.js reads. A question needing conversion still needs a
     * bespoke renderer above. */
    function genericStep(key, kind) {
      var cfg = q(key);

      if (kind === 'number') {
        var limits = limitsFor(key);
        questionStep({
          title: cfg.title || key,
          hint: cfg.hint || null,
          fields: [{ key: 'v', label: (unitsFor(key) && unitsFor(key)[0] ? unitsFor(key)[0].label : ''), placeholder: '', value: state.inputs[key] != null ? state.inputs[key] : '' }],
          onNext: function (values) {
            var n = parseFloat(values.v);
            if (!within(n, limits)) {
              return 'Please enter a value between ' + limits[0] + ' and ' + limits[1] + '.';
            }
            state.inputs[key] = n;
            advance(key);
          }
        });
        return;
      }

      var opts = optionsFor(key);
      var subs = sublabelsFor(key);
      choiceStep({
        eyebrow: null,
        title: cfg.title || key,
        hint: cfg.hint || null,
        layout: kind === 'choice_grid' ? 'grid' : 'list',
        options: opts.map(function (value, i) {
          return { value: value, label: String(value), sub: subs[i] || null };
        }),
        initial: state.inputs[key],
        ctaLabel: 'Next',
        onNext: function (value) {
          state.inputs[key] = value;
          advance(key);
        }
      });
    }

    /* --------------------------------------------------- standard sizes */

    function renderStandard() {
      /* Each button carries both readings: the size on top, the chest it is
       * cut to fit underneath. This screen used to show one or the other
       * behind an Inches / XS-2XL toggle, which made a customer who knows
       * their chest but not our letter sizes work the toggle before they
       * could choose - a click, and a moment of doubt, on the shortest path
       * in the panel. */
      choiceStep({
        eyebrow: null,
        title: 'Standard sizes',
        hint: 'The chest each size is cut to fit, in inches.',
        layout: 'grid',
        options: standardSizeValues().map(function (value) {
          var available = config.hasBuildOption
            ? true // availability depends on the Build chosen next
            : sellable(findVariant(value, null));
          // Reuses the same inch-range <-> alpha mapping as the chest
          // question (CHEST_SIZES); a value with no match (e.g. a store Size
          // option that isn't on the flow's ladder) simply has no second line.
          var chestPair = CHEST_SIZES.find(function (p) { return p[1] === value; });
          return {
            value: value,
            label: value,
            sub: chestPair ? chestPair[0] : '',
            disabled: !available
          };
        }),
        initial: state.inputs.standardSizeValue,
        // The size step is the last one when the product has no length to choose.
        ctaLabel: config.hasBuildOption ? 'Next' : ADD_LABEL,
        onNext: function (value) {
          state.inputs.standardSizeValue = value;
          if (config.hasBuildOption) {
            state.fromCompare = false;
            goTo('standardBuild');
          } else {
            var variant = findVariant(value, null);
            if (!sellable(variant)) {
              state.variantError = value + ' is not available at the moment.';
              render();
              return;
            }
            capture.send(ADD_STATUS, { path: 'standard', chosenSize: value, variantId: variant.id });
            addToCart(variant.id, { 'Size type': 'Standard', 'Size': value });
          }
        }
      });
    }

    function renderStandardBuild() {
      // Reached from the Standard path and from Compare. The hint says which
      // size we are choosing a length for either way.
      var fromCompare = state.fromCompare;
      choiceStep({
        eyebrow: null,
        title: 'Choose your build',
        hint: fromCompare
          ? 'Size ' + state.inputs.standardSizeValue + ', closest to the garment you measured.'
          : 'Size ' + state.inputs.standardSizeValue + '.',
        layout: 'grid',
        options: (config.buildValues || []).map(function (value) {
          var variant = findVariant(state.inputs.standardSizeValue, value);
          return { value: value, label: value, disabled: !sellable(variant) };
        }),
        initial: state.inputs.buildValue,
        ctaLabel: ADD_LABEL,
        onNext: function (value) {
          state.inputs.buildValue = value;
          var variant = findVariant(state.inputs.standardSizeValue, value);
          if (!sellable(variant)) {
            state.variantError = state.inputs.standardSizeValue + ' in ' + value + ' is not available at the moment.';
            render();
            return;
          }
          capture.send(ADD_STATUS, {
            path: fromCompare ? 'compare' : 'standard',
            compare: fromCompare ? { points: state.compare, closest: state.inputs.standardSizeValue } : null,
            chosenSize: state.inputs.standardSizeValue,
            chosenBuild: value,
            variantId: variant.id
          });
          var buildProps = {
            'Size type': fromCompare ? 'Compared' : 'Standard',
            'Size': state.inputs.standardSizeValue,
            'Build': value
          };
          if (fromCompare) {
            Object.keys(state.compare).forEach(function (label) {
              buildProps['Their ' + label.toLowerCase() + ' (cm)'] = String(state.compare[label]);
            });
          }
          addToCart(variant.id, buildProps);
        }
      });
    }

    /* ----------------------------------------------- custom flow questions */

    function renderHeight() {
      questionStep({
        title: q('heightCm').title || 'How tall are you?',
        hint: q('heightCm').hint || (config.hasBuildOption ? 'Our made-to-order coats are cut in three lengths to suit most builds.' : null),
        unitToggle: {
          options: unitsFor('heightCm'),
          active: state.units.height,
          onChange: function (u) { state.units.height = u; saveJSON(LS_UNITS, state.units); render(); }
        },
        fields: state.units.height === 'cm'
          ? [{ key: 'cm', label: 'cm', placeholder: '—', value: state.inputs.heightCm || '' }]
          : ftinFields(),
        onNext: function (values) {
          var cm = state.units.height === 'cm'
            ? parseFloat(values.cm)
            : Math.round((parseFloat(values.ft || 0) * 30.48 + parseFloat(values.inch || 0) * 2.54) * 10) / 10;
          if (!within(cm, limitsFor('heightCm'))) return 'Please enter a height between ' + limitText('heightCm', 'cm');
          state.inputs.heightCm = cm;
          advance('heightCm');
        }
      });

      function ftinFields() {
        var cm = state.inputs.heightCm;
        var ft = '', inch = '';
        if (cm) {
          var totalIn = cm / 2.54;
          ft = Math.floor(totalIn / 12);
          inch = Math.round(totalIn % 12);
        }
        return [
          { key: 'ft', label: 'ft', placeholder: '5', value: ft },
          { key: 'inch', label: 'in', placeholder: '11', value: inch }
        ];
      }
    }

    function renderWeight() {
      questionStep({
        title: q('weightKg').title || 'How much do you weigh?',
        hint: q('weightKg').hint || 'This, with your height, gives us your overall build.',
        unitToggle: {
          options: unitsFor('weightKg'),
          active: state.units.weight,
          onChange: function (u) { state.units.weight = u; saveJSON(LS_UNITS, state.units); render(); }
        },
        fields: [{
          key: 'w',
          label: state.units.weight,
          placeholder: '—',
          value: state.inputs.weightKg
            ? (state.units.weight === 'kg' ? state.inputs.weightKg : Math.round(state.inputs.weightKg * 2.20462))
            : ''
        }],
        onNext: function (values) {
          var kg = state.units.weight === 'kg'
            ? parseFloat(values.w)
            : Math.round(parseFloat(values.w) / 2.20462 * 10) / 10;
          if (!within(kg, limitsFor('weightKg'))) return 'Please enter a weight between ' + limitText('weightKg', 'kg');
          state.inputs.weightKg = kg;
          advance('weightKg');
        }
      });
    }

    function renderShoe() {
      questionStep({
        title: q('shoeSizeEu').title || 'What is your shoe size?',
        hint: q('shoeSizeEu').hint || 'A fuller picture of your proportions.',
        unitToggle: {
          options: unitsFor('shoeSizeEu'),
          active: state.units.shoe,
          onChange: function (u) { state.units.shoe = u; saveJSON(LS_UNITS, state.units); render(); }
        },
        fields: [{
          key: 's',
          label: state.units.shoe,
          placeholder: '—',
          value: state.inputs.shoeSizeEu ? fromEuShoe(state.inputs.shoeSizeEu, state.units.shoe) : ''
        }],
        onNext: function (values) {
          var eu = toEuShoe(parseFloat(values.s), state.units.shoe);
          if (!within(eu, limitsFor('shoeSizeEu'))) return 'Please enter a valid shoe size';
          state.inputs.shoeSizeEu = eu;
          advance('shoeSizeEu');
        }
      });
    }

    function renderChest() {
      /* Inches only. This screen asks what the customer's chest measures, and
       * offering to answer it in our size letters instead invited them to
       * guess at a label rather than report a measurement, which is the one
       * input the recommendation is built on. Their size is what the panel
       * works out at the end; it is not a question. */
      choiceStep({
        title: q('chestRange').title || 'What is your chest size?',
        hint: q('chestRange').hint || 'Measured around the fullest part of your chest, in inches.',
        layout: 'grid',
        options: CHEST_SIZES.map(function (pair) {
          return { value: pair[0], label: pair[0] };
        }),
        initial: state.inputs.chestRange,
        ctaLabel: 'Next',
        onNext: function (value) {
          var i = CHEST_SIZES.findIndex(function (p) { return p[0] === value; });
          state.inputs.chestIdx = i;
          state.inputs.chestRange = CHEST_SIZES[i][0];
          state.inputs.chestAlpha = CHEST_SIZES[i][1];
          advance('chestRange');
        }
      });
    }

    function renderCollar() {
      /* Shirt collars are sold in inches here and in centimetres elsewhere,
       * and a customer reading their own shirt label should not have to
       * convert it in their head. The value stored is always the inch size:
       * only the label changes. */
      var unit = state.units.collar || 'in';
      choiceStep({
        title: q('collarIn').title || 'What is your collar size?',
        hint: q('collarIn').hint || 'Measured around the base of your neck.',
        unitToggle: {
          options: [{ id: 'in', label: 'Inches' }, { id: 'cm', label: 'CM' }],
          active: unit,
          onChange: function (u) { state.units.collar = u; saveJSON(LS_UNITS, state.units); render(); }
        },
        layout: 'grid',
        options: optionsFor('collarIn').map(function (size) {
          var n = Number(size);
          return { value: n, label: unit === 'cm' ? inchesToCmLabel(n) : fmtHalf(n) };
        }),
        initial: state.inputs.collarIn,
        ctaLabel: 'Next',
        onNext: function (value) {
          state.inputs.collarIn = value;
          advance('collarIn');
        }
      });
    }

    function renderWaist() {
      var unit = state.units.waist || 'in';
      choiceStep({
        title: q('waistIn').title || 'What is your waist size?',
        hint: q('waistIn').hint || 'As you would buy trousers.',
        unitToggle: {
          options: [{ id: 'in', label: 'Inches' }, { id: 'cm', label: 'CM' }],
          active: unit,
          onChange: function (u) { state.units.waist = u; saveJSON(LS_UNITS, state.units); render(); }
        },
        layout: 'grid',
        options: optionsFor('waistIn').map(function (size) {
          var n = Number(size);
          return { value: n, label: unit === 'cm' ? inchesToCmLabel(n) : String(n) };
        }),
        initial: state.inputs.waistIn,
        ctaLabel: 'Next',
        onNext: function (value) {
          state.inputs.waistIn = value;
          advance('waistIn');
        }
      });
    }

    function renderFit() {
      choiceStep({
        title: q('fit').title || 'How do you like your fit?',
        hint: q('fit').hint || null,
        layout: 'list',
        options: optionsFor('fit').map(function (value, i) {
          return {
            value: value,
            label: String(value).charAt(0).toUpperCase() + String(value).slice(1),
            sub: sublabelsFor('fit')[i] || ''
          };
        }),
        initial: state.inputs.fit,
        ctaLabel: 'See my size',
        onNext: function (value) {
          state.inputs.fit = value;
          advance('fit');
        }
      });
    }

    function renderLoading() {
      var wrap = el('div', 'csf__loading');
      wrap.appendChild(el('div', 'csf__spinner', ''));
      wrap.appendChild(el('p', 'csf__hint', 'Calculating your custom size…'));
      body.appendChild(wrap);
    }

    function renderError() {
      body.appendChild(el('h1', 'csf__title', 'Something went wrong'));
      body.appendChild(el('p', 'csf__hint', 'We could not calculate your size. Please try again.'));
      var retry = el('button', 'csf__next', 'Retry');
      retry.type = 'button';
      retry.addEventListener('click', function () { goTo('loading'); computeResult(); });
      body.appendChild(retry);
    }

    /* -------------------------------------------------------------- review */

    function renderReview() {
      var r = state.result;
      var chosen = state.chosen;
      var moved = chosen.size !== r.recommendedSize || chosen.build !== r.recommendedBuild;

      body.appendChild(el('h1', 'csf__title', 'Your recommended size'));

      var recBox = el('div', 'csf__rec-box');
      /* Once the customer has moved off the recommendation the headline stops
       * claiming to be a recommendation. Saying "We recommend XL" after they
       * pushed it there themselves is a small lie, and on this product it is
       * the kind that ends in a returned coat and a conversation about who
       * chose the size. */
      // Spelt out, as in the booking note (see sizeWords), with the dot kept on screen.
      var headline = (moved ? 'Your selection: ' : 'We recommend ') + sizeWords(chosen.size, chosen.build, ' · ');
      recBox.appendChild(el('div', 'csf__rec-headline', headline));
      /* Built from what was actually answered. Hardcoding all eight made the
       * Anson's five-question flow read
       *   "40-42" chest (L) · null" collar · null" waist"
       * on screen - the same fault as the line item properties, in the place
       * the customer can see. */
      var line1 = [];
      if (state.inputs.heightCm != null) line1.push(heightLabel());
      if (state.inputs.weightKg != null) line1.push(weightLabel());
      if (state.inputs.fit) line1.push(fitLabel() + ' fit');

      var line2 = [];
      if (state.inputs.chestRange) {
        /* The chest is reported as they gave it, with no size letter beside
         * it. The letter here was the size that range maps to on the ladder,
         * which is not always the size recommended above it: ask for a slim
         * fit and the two disagree, on the one screen where a customer is
         * checking whether we understood them. */
        line2.push(state.inputs.chestRange + '" chest');
      }
      if (state.inputs.collarIn != null) line2.push(state.inputs.collarIn + '" collar');
      if (state.inputs.waistIn != null) line2.push(state.inputs.waistIn + '" waist');

      var detailLines = [];
      if (line1.length) detailLines.push(line1.join(' · '));
      if (line2.length) detailLines.push(line2.join(' · '));
      if (moved) {
        detailLines.push('We recommended ' + sizeWords(r.recommendedSize, r.recommendedBuild, ' · ') + '.');
      }
      var detailEl = el('div', 'csf__rec-detail');
      detailLines.forEach(function (line, i) {
        if (i > 0) detailEl.appendChild(document.createElement('br'));
        detailEl.appendChild(document.createTextNode(line));
      });
      recBox.appendChild(detailEl);

      /* The check on the recommendation: the garment's own finished chest
       * against the customer's, in centimetres they can hold a tape to. */
      var roomText = roomDetail(chosen.size);
      if (roomText) recBox.appendChild(el('div', 'csf__rec-room', roomText));

      body.appendChild(recBox);

      // Collapsed: a single bordered toggle row. Expanded: that row is
      // replaced entirely by the numbers panel (which has its own "The
      // numbers" heading + cm/inches switch) - the two are mutually
      // exclusive per the design, not a disclosure that stays visible
      // alongside its contents.
      if (!state.numbersExpanded) {
        var numbersToggle = document.createElement('button');
        numbersToggle.type = 'button';
        numbersToggle.className = 'csf__numbers-toggle';
        numbersToggle.appendChild(el('span', '', 'Check the garment measurements'));
        numbersToggle.appendChild(el('span', 'csf__numbers-arrow', '→'));
        numbersToggle.addEventListener('click', function () {
          state.numbersExpanded = true;
          render();
        });
        body.appendChild(numbersToggle);
      } else {
        body.appendChild(renderNumbersPanel());
      }

      body.appendChild(el('div', 'csf__eyebrow-label', 'Not quite right?'));
      var adjustGrid = el('div', 'csf__adjust-grid');
      adjustGrid.appendChild(adjustBtn('− Size down', function () { adjustSize(-1); }, sizeIndex() <= 0));
      adjustGrid.appendChild(adjustBtn('+ Size up', function () { adjustSize(1); }, sizeIndex() >= SIZE_LADDER.length - 1 || sizeIndex() === -1));
      if (config.hasBuildOption) {
        adjustGrid.appendChild(adjustBtn('− Shorter', function () { adjustBuild(-1); }, buildIndex() <= 0));
        adjustGrid.appendChild(adjustBtn('+ Longer', function () { adjustBuild(1); }, buildIndex() >= (config.buildValues || []).length - 1 || buildIndex() === -1));
      }
      body.appendChild(adjustGrid);

      /* No endpoint configured means nothing can be sent, so the row is not
       * offered. Showing a Send button that always fails is the same category
       * of fault as the stub that claimed success. */
      if (config.captureEndpoint && !WAITLIST) body.appendChild(renderEmailCapture());

      if (state.variantError) {
        var warn = el('p', 'csf__error', state.variantError);
        warn.setAttribute('role', 'alert');
        body.appendChild(warn);
      }

      /* The answers behind this recommendation are mostly remembered rather
       * than measured: a trouser label for the waist, a shirt label for the
       * collar. So the screen says a person looks at them before anything is
       * made, and offers the call again here, to the customer who has just
       * finished the questions and is still not sure. */
      if (!WAITLIST) {
        body.appendChild(el('p', 'csf__hint csf__assure',
          'We check every set of measurements by hand before anything is made, and will be in touch if yours need a second look.'));
      }

      var add = el('button', 'csf__next', ADD_LABEL);
      add.type = 'button';
      add.addEventListener('click', submitCustom);
      body.appendChild(add);

      if (canBook()) {
        var call = document.createElement('button');
        call.type = 'button';
        call.className = 'csf__numbers-toggle csf__book-row';
        call.appendChild(el('span', '', 'Or book a call to check your size'));
        var callArrow = el('span', 'csf__numbers-arrow', '→');
        callArrow.setAttribute('aria-hidden', 'true');
        call.appendChild(callArrow);
        call.addEventListener('click', openBooking);
        body.appendChild(call);
      }

      function adjustBtn(label, handler, disabled) {
        var b = el('button', 'csf__adjust-btn', label);
        b.type = 'button';
        if (disabled) {
          b.disabled = true;
        } else {
          b.addEventListener('click', function () { handler(); render(); });
        }
        return b;
      }

      function sizeIndex() { return SIZE_LADDER.indexOf(chosen.size); }
      function buildIndex() { return (config.buildValues || []).indexOf(chosen.build); }
      function adjustSize(delta) {
        var i = sizeIndex();
        if (i === -1) return;
        var next = Math.min(SIZE_LADDER.length - 1, Math.max(0, i + delta));
        state.chosen.size = SIZE_LADDER[next];
        var chest = chartChestFor(state.chosen.size);
        if (chest != null) state.chosen.chestCm = chest;
        reportAdjustment();
      }
      function adjustBuild(delta) {
        var vals = config.buildValues || [];
        var i = buildIndex();
        if (i === -1) return;
        var next = Math.min(vals.length - 1, Math.max(0, i + delta));
        state.chosen.build = vals[next];
        reportAdjustment();
      }
      /* Whether customers accept the recommendation or talk themselves out of
       * it is the single most useful signal here - it is how you find out the
       * algorithm runs a size small before the returns tell you. */
      function reportAdjustment() {
        capture.sendAdjustDebounced({
          path: 'find',
          inputs: capturedInputs(),
          result: state.result,          // untouched: what the algorithm said
          chosenSize: state.chosen.size, // where the customer moved it
          chosenBuild: state.chosen.build
        });
      }
    }

    function renderNumbersPanel() {
      var wrap = el('div', 'csf__numbers-panel');
      var head = el('div', 'csf__numbers-head');
      head.appendChild(el('span', 'csf__eyebrow-label', 'The numbers'));
      var seg = el('div', 'csf__seg');
      ['cm', 'in'].forEach(function (u) {
        var b = el('button', u === state.numbersUnit ? 'is-on' : '', u === 'cm' ? 'CM' : 'Inches');
        b.type = 'button';
        b.addEventListener('click', function () { state.numbersUnit = u; render(); });
        seg.appendChild(b);
      });
      head.appendChild(seg);
      wrap.appendChild(head);

      var rows = el('div', 'csf__numbers-rows');
      var shown = measurementsForSize(state.chosen.size);

      if (shown.length) {
        shown.forEach(function (m) {
          var display = fmtSpec(m.cm);
          var row = el('div', 'csf__numbers-row');
          row.appendChild(el('span', '', m.label));
          row.appendChild(el('span', '', display));
          rows.appendChild(row);
        });
      } else {
        // No published table for this flow - fall back to the single estimated
        // chest rather than showing nothing.
        var cm = state.chosen.chestCm != null ? state.chosen.chestCm : state.result.measurements.chestCm;
        if (cm != null && !isNaN(cm)) {
          var row = el('div', 'csf__numbers-row');
          row.appendChild(el('span', '', 'Chest'));
          row.appendChild(el('span', '', state.numbersUnit === 'cm'
            ? round1(cm) + ' cm' : round1(cm / 2.54) + ' in'));
          rows.appendChild(row);
        }
      }
      wrap.appendChild(rows);

      // These are flat garment measurements, sat directly under the customer's
      // own body measurements. Without saying so, a 51.5 cm chest under a
      // stated 38" chest reads as an error.
      wrap.appendChild(el('p', 'csf__hint', shown.length
        ? 'Finished garment, measured flat. These are the specifications your ' +
          state.chosen.size + ' is made to.'
        : 'Finished garment, measured flat across the chest.'));
      return wrap;
    }

    function renderEmailCapture() {
      var wrap = el('div', 'csf__email-row');
      wrap.appendChild(el('div', 'csf__eyebrow-label', 'Email me this recommendation'));
      var fieldRow = el('div', 'csf__email-field-row');
      var input = document.createElement('input');
      input.type = 'email';
      input.className = 'csf__input csf__input--underline';
      input.placeholder = 'your@email.com';
      input.value = state.emailValue;
      input.addEventListener('input', function () { state.emailValue = input.value; });
      var send = el('button', 'csf__email-send', 'Send');
      send.type = 'button';
      send.addEventListener('click', submitEmailCapture);
      fieldRow.appendChild(input);
      fieldRow.appendChild(send);
      wrap.appendChild(fieldRow);
      if (state.emailStatus === 'sending') {
        wrap.appendChild(el('p', 'csf__hint', 'Sending…'));
      } else if (state.emailStatus === 'confirmed') {
        wrap.appendChild(el('p', 'csf__hint', 'Sent. Check your inbox for your size and the garment measurements.'));
      } else if (state.emailStatus === 'invalid') {
        wrap.appendChild(el('p', 'csf__error', 'Please enter a valid email address.'));
      } else if (state.emailStatus === 'failed') {
        wrap.appendChild(el('p', 'csf__error', 'We could not send that just now. Your size is shown above.'));
      } else {
        /* Consent, stated at the point of collection rather than buried. The
         * address is used to send this recommendation and is kept with the
         * sizing record; anything beyond that would need a separate opt-in. */
        var consent = el('p', 'csf__consent', 'We will use this to send your recommendation and keep it with your sizing record. ');
        var policy = document.createElement('a');
        policy.href = '/policies/privacy-policy';
        policy.target = '_blank';
        policy.rel = 'noopener';
        policy.textContent = 'Privacy policy';
        consent.appendChild(policy);
        consent.appendChild(document.createTextNode('.'));
        wrap.appendChild(consent);
      }
      return wrap;

      /* Replaces a stub that validated the address, told the customer "Thanks,
       * we have noted your details", and then wrote it to the browser console
       * and discarded it on unload. That was a false statement to a customer
       * who had handed something over on the strength of it. This posts the
       * address with the sizing record to the capture endpoint, which emails
       * the customer their recommendation and files the row. */
      function submitEmailCapture() {
        var address = state.emailValue.trim();
        var valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);
        if (!valid) {
          state.emailStatus = 'invalid';
          render();
          return;
        }
        state.emailStatus = 'sending';
        render();
        capture.send('email_captured', {
          path: state.compareResult ? 'compare' : 'find',
          email: address,
          emailConsent: true,
          inputs: capturedInputs(),
          result: state.result,
          chosenSize: state.chosen ? state.chosen.size : null,
          chosenBuild: state.chosen ? state.chosen.build : null,
          compare: state.compareResult ? { points: state.compare, closest: state.compareResult.size } : null
        });
        /* The endpoint is fire-and-forget by design (see the capture module),
         * so there is no response to wait on. Confirm optimistically after a
         * beat. If the send failed, the customer still has their size on
         * screen and the studio still has the row from result_reached. */
        setTimeout(function () {
          if (state.emailStatus === 'sending') {
            state.emailStatus = 'confirmed';
            render();
          }
        }, 900);
      }
    }

    /* ------------------------------------------------------------ waitlist
     *
     * The last screen of every path in waitlist mode. First name and email
     * only; the size was chosen on the way here.
     *
     * The signup is Shopify's own customer form (the same one the footer
     * newsletter uses), rendered hidden by the snippet so it carries the
     * right action and form_type. It creates or updates the customer as an
     * email subscriber with the tags below, and the email platform starts
     * its flow from the waitlist tag being added. That needs no server of
     * ours, which is what kept "Email me this recommendation" on the capture
     * endpoint (see the note at the top of this file).
     *
     * It is submitted as a real form, so the page reloads. A background
     * fetch was tried first and Shopify refuses it outright ("Missing
     * CAPTCHA token"): its bot check only runs on a genuine submit, and may
     * put its own "confirm you are not a robot" page in between. Either way
     * Shopify then comes back to this page, which reopens the panel on the
     * confirmation (see root.csfOpen above).
     */
    function waitlistForm() { return root.querySelector('[data-csf-waitlist-form]'); }

    /* Reached only by the side doors (the Size guide and Compare my size
     * drawers can still land on a size): the size is already known, so this
     * is the form without the picker. The front door is the first screen,
     * which carries the whole form under the intro - see renderMode. */
    function renderWaitlist() {
      var short = config.productShortName || config.productTitle || '';
      body.appendChild(el('h1', 'csf__title', 'Join the ' + short + ' waitlist'));
      body.appendChild(el('p', 'csf__hint',
        (state.waitlistSize ? 'Size ' + state.waitlistSize + '. ' : '') +
        'Leave your name and email and we will write to you when the order books open.'));
      appendWaitlistForm(false);
    }

    /* The form itself: first name, email, and (on the first screen) the size
     * as one-tap buttons rather than a dropdown - four options are quicker to
     * read and choose laid out than hidden behind a menu, and each shows the
     * chest it is cut to fit, which is the only sizing help most people need.
     *
     * Picking a size does not repaint: a repaint would rebuild the inputs and
     * drop the keyboard on a phone mid-form. The buttons toggle in place. */
    function appendWaitlistForm(withSizePicker) {
      var short = config.productShortName || config.productTitle || '';

      var nameInput = document.createElement('input');
      nameInput.type = 'text';
      nameInput.className = 'csf__input csf__input--underline';
      nameInput.placeholder = 'First name';
      nameInput.autocomplete = 'given-name';
      nameInput.setAttribute('aria-label', 'First name');
      nameInput.value = state.waitlistName;
      nameInput.addEventListener('input', function () { state.waitlistName = nameInput.value; });

      var emailInput = document.createElement('input');
      emailInput.type = 'email';
      emailInput.className = 'csf__input csf__input--underline';
      emailInput.placeholder = 'your@email.com';
      emailInput.autocomplete = 'email';
      emailInput.setAttribute('aria-label', 'Email address');
      emailInput.value = state.waitlistEmail;
      emailInput.addEventListener('input', function () { state.waitlistEmail = emailInput.value; });

      var fields = el('div', 'csf__waitlist-fields');
      fields.style.display = 'grid';
      fields.style.gap = '20px';
      fields.style.margin = '24px 0';
      fields.appendChild(nameInput);
      fields.appendChild(emailInput);
      body.appendChild(fields);

      var sizes = withSizePicker ? standardSizeValues() : [];
      state.waitlistNeedsSize = sizes.length > 0;
      if (sizes.length) {
        var sizeLabel = el('div', 'csf__eyebrow-label', 'Your size');
        sizeLabel.id = 'csf-waitlist-size-label';
        body.appendChild(sizeLabel);
        var grid = el('div', 'csf__size-grid');
        grid.setAttribute('role', 'radiogroup');
        grid.setAttribute('aria-labelledby', sizeLabel.id);
        grid.style.marginBottom = '24px';
        // Up to four sizes sit on one row rather than wrapping the last one alone.
        if (sizes.length <= 4) grid.style.gridTemplateColumns = 'repeat(' + sizes.length + ', 1fr)';
        var sizeBtns = [];
        sizes.forEach(function (value) {
          var chestPair = CHEST_SIZES.find(function (pair) { return pair[1] === value; });
          var btn = el('button', 'csf__size-btn' + (state.waitlistSize === value ? ' is-selected' : ''));
          btn.type = 'button';
          btn.setAttribute('role', 'radio');
          btn.setAttribute('aria-checked', state.waitlistSize === value ? 'true' : 'false');
          btn.appendChild(el('span', 'csf__size-label', value));
          if (chestPair) btn.appendChild(el('span', 'csf__size-sub', chestPair[0] + '"'));
          btn.addEventListener('click', function () {
            state.waitlistSize = value;
            sizeBtns.forEach(function (other) {
              var on = other === btn;
              other.classList.toggle('is-selected', on);
              other.setAttribute('aria-checked', on ? 'true' : 'false');
            });
          });
          sizeBtns.push(btn);
          grid.appendChild(btn);
        });
        body.appendChild(grid);
      }

      if (state.waitlistStatus === 'invalid') {
        var bad = el('p', 'csf__error', state.waitlistNeedsSize
          ? 'Please enter your first name and a valid email address, and choose a size.'
          : 'Please enter your first name and a valid email address.');
        bad.setAttribute('role', 'alert');
        body.appendChild(bad);
      } else if (state.waitlistStatus === 'stalled') {
        var stalled = el('p', 'csf__error', 'That is taking longer than it should. Please refresh the page and try again.');
        stalled.setAttribute('role', 'alert');
        body.appendChild(stalled);
      } else if (state.waitlistStatus === 'failed') {
        var failed = el('p', 'csf__error', 'We could not add you just now. Please try again.');
        failed.setAttribute('role', 'alert');
        body.appendChild(failed);
      }

      var join = el('button', 'csf__next', state.waitlistStatus === 'sending' ? 'Joining…' : 'Join the waitlist');
      join.type = 'button';
      join.disabled = state.waitlistStatus === 'sending';
      join.addEventListener('click', submitWaitlist);
      body.appendChild(join);

      [nameInput, emailInput].forEach(function (input) {
        input.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); submitWaitlist(); }
        });
      });

      /* Consent, stated at the point of collection. Joining subscribes the
       * address to email, so it says so. */
      var consent = el('p', 'csf__consent',
        'By joining you agree to receive emails from Barnfield about the ' + short + '. Unsubscribe at any time. ');
      var policy = document.createElement('a');
      policy.href = '/policies/privacy-policy';
      policy.target = '_blank';
      policy.rel = 'noopener';
      policy.textContent = 'Privacy policy';
      consent.appendChild(policy);
      consent.appendChild(document.createTextNode('.'));
      body.appendChild(consent);
    }

    function submitWaitlist() {
      if (state.waitlistStatus === 'sending') return;
      var name = state.waitlistName.trim();
      var address = state.waitlistEmail.trim();
      if (!name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address) ||
          (state.waitlistNeedsSize && !state.waitlistSize)) {
        state.waitlistStatus = 'invalid';
        render();
        return;
      }
      var formEl = waitlistForm();
      if (!formEl) {
        console.warn('[csf] waitlist form missing from the page');
        state.waitlistStatus = 'failed';
        render();
        return;
      }

      var tags = [config.waitlistTag];
      if (state.waitlistSize && config.waitlistSizeTagPrefix) {
        tags.push(config.waitlistSizeTagPrefix + state.waitlistSize);
      }
      formEl.querySelector('[name="contact[first_name]"]').value = name;
      formEl.querySelector('[name="contact[email]"]').value = address;
      formEl.querySelector('[name="contact[tags]"]').value = tags.filter(Boolean).join(', ');

      state.waitlistStatus = 'sending';
      render();

      capture.send('waitlist_joined', {
        path: state.compareResult ? 'compare' : 'waitlist_form',
        email: address,
        firstName: name,
        emailConsent: true,
        inputs: capturedInputs(),
        result: state.result,
        chosenSize: state.waitlistSize,
        tags: tags
      });

      /* Remembered across the round trip, so the page that Shopify sends the
       * customer back to knows the signup was this form and not, say, the
       * footer newsletter (which comes back with the same marker). */
      try { sessionStorage.setItem(WAITLIST_PENDING_KEY, config.productHandle || '1'); } catch (e) {}

      /* The email platform is told directly, first. Shopify's customer form
       * (below) is still what records consent on the customer, but the sync
       * from Shopify to the email platform was seen to lag by more than
       * twenty minutes, and the waitlist flow starts from the tag arriving.
       * The form is submitted once this has answered, or after 2.5 seconds
       * regardless: the webhook must never be able to hold up the signup. */
      var submitted = false;
      function submitToShopify() {
        if (submitted) return;
        submitted = true;
        /* A real submit, not a fetch. requestSubmit() fires the submit event
         * that Shopify's bot check listens for; form.submit() would bypass it
         * and be refused. From here the browser leaves the page. */
        try {
          if (typeof formEl.requestSubmit === 'function') formEl.requestSubmit();
          else formEl.submit();
        } catch (err) {
          console.warn('[csf] waitlist signup failed:', err);
          state.waitlistStatus = 'failed';
          render();
        }
      }

      if (config.waitlistWebhook) {
        setTimeout(submitToShopify, 2500);
        try {
          fetch(config.waitlistWebhook, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: address,
              first_name: name,
              firstName: name,
              tags: tags.filter(Boolean),
              source: (config.productShortName || '') + ' waitlist'
            }),
            keepalive: true,
            mode: 'cors'
          }).catch(function (err) {
            console.warn('[csf] waitlist webhook failed (ignored):', err);
          }).then(submitToShopify);
        } catch (err) {
          console.warn('[csf] waitlist webhook threw (ignored):', err);
          submitToShopify();
        }
      } else {
        submitToShopify();
      }

      /* Still here after a while: the submit went nowhere (the bot check
       * could not load, most likely). Give the button back rather than
       * leaving it saying "Joining…" for good. */
      setTimeout(function () {
        if (state.waitlistStatus === 'sending') {
          state.waitlistStatus = 'stalled';
          if (state.step === 'mode' || state.step === 'waitlist') render();
        }
      }, 12000);
    }

    /* Shopify only arms its bot check on a form the customer has touched
     * (it listens for focus and change inside the form), and swallows the
     * submit of one it has not armed. This form is hidden and filled in by
     * script, so nobody ever touches it: it has to be armed by hand, when
     * the panel opens, or Join the waitlist does nothing at all. */
    function armWaitlistForm() {
      var formEl = waitlistForm();
      if (!formEl || formEl.dataset.csfArmed) return;
      formEl.dataset.csfArmed = 'true';
      try {
        if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
          window.Shopify.captcha.protect(formEl, function () {});
        } else {
          formEl.dispatchEvent(new Event('focusin', { bubbles: true }));
        }
      } catch (err) {
        console.warn('[csf] could not arm the waitlist form:', err);
      }
    }

    function waitlistJoined() {
      state.waitlistStatus = null;
      state.step = 'waitlistDone';
      state.history = [];
      panel.scrollTop = 0;
      renderWithFade();
    }

    function renderWaitlistDone() {
      var short = config.productShortName || config.productTitle || '';
      body.appendChild(el('h1', 'csf__title', 'You are on the list'));
      body.appendChild(el('p', 'csf__hint',
        'Thank you. We will write to you as the ' + short + ' takes shape, and again when the order books open.'));
      /* The intro's own link (the Ledger, on a product in development) is
       * the natural next place to send someone who has just signed up. */
      var introLink = /\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)\s]+)\)/.exec(config.panelSubtext || '');
      if (introLink) {
        var more = el('p', 'csf__hint');
        more.appendChild(document.createTextNode('In the meantime, read '));
        var link = document.createElement('a');
        link.href = introLink[2];
        link.textContent = introLink[1];
        link.style.color = 'inherit';
        more.appendChild(link);
        more.appendChild(document.createTextNode('.'));
        body.appendChild(more);
      }
      var done = el('button', 'csf__next', 'Close');
      done.type = 'button';
      done.addEventListener('click', close);
      body.appendChild(done);
    }

    /* Plain text from the admin, with two allowances: a blank line starts a
     * new paragraph, and [label](url) becomes a link. Built with DOM nodes
     * and textContent throughout, so nothing typed into the admin field is
     * ever parsed as HTML. Only http(s) and site-relative URLs are linked. */
    function appendRichText(parent, text, className) {
      String(text).split(/\n\s*\n/).forEach(function (para) {
        if (!para.trim()) return;
        var p = el('p', className);
        var re = /\[([^\]]+)\]\(([^)\s]+)\)/g;
        var last = 0;
        var m;
        while ((m = re.exec(para)) !== null) {
          if (m.index > last) p.appendChild(document.createTextNode(para.slice(last, m.index)));
          if (/^(https?:\/\/|\/)/.test(m[2])) {
            var a = document.createElement('a');
            a.href = m[2];
            a.textContent = m[1];
            a.style.color = 'inherit';
            p.appendChild(a);
          } else {
            p.appendChild(document.createTextNode(m[1]));
          }
          last = re.lastIndex;
        }
        if (last < para.length) p.appendChild(document.createTextNode(para.slice(last)));
        parent.appendChild(p);
      });
    }

    /* ------------------------------------------------------------- actions */

    function computeResult() {
      if (!window.CustomSizing || typeof window.CustomSizing.computeSize !== 'function') {
        console.error('[csf] window.CustomSizing.computeSize is missing — check size-algorithm.js is loaded.');
        state.step = 'error';
        renderWithFade();
        return;
      }
      window.CustomSizing.computeSize(Object.assign({}, state.inputs), {
        sizeChart: config.sizeChart,
        easeSlim: config.easeSlim,
        easeRegular: config.easeRegular,
        easeRelaxed: config.easeRelaxed,
        sizeLadder: SIZE_LADDER,
        algorithm: config.algorithm
      })
        .then(function (result) {
          if (!result || !result.measurements) throw new Error('Malformed algorithm result');
          state.result = result;
          state.chosen = {
            size: result.recommendedSize,
            build: result.recommendedBuild,
            chestCm: result.measurements ? result.measurements.chestCm : null
          };
          state.step = 'review';
          state.history = [CUSTOM_STEPS[CUSTOM_STEPS.length - 1]];
          state.numbersExpanded = false;
          capture.send('result_reached', {
            path: 'find',
            inputs: capturedInputs(),
            result: result
          });
          renderWithFade();
        })
        .catch(function (err) {
          console.error('[csf] computeSize failed:', err);
          state.step = 'error';
          renderWithFade();
        });
    }

    function submitCustom() {
      var name = state.sizeName || 'Custom size';
      persistSavedSize(name);

      /* Properties follow the questions the flow actually asked, not a fixed
       * list. Previously all eight were written unconditionally, so a flow that
       * dropped the shoe question shipped "Shoe size (EU): null" and
       * "Height: undefined cm" onto a real order. Only answered inputs are
       * written now, and a question configured with a new inputKey gets a
       * readable label rather than being lost. */
      var props = {
        'Size type': 'Custom',
        'Size name': name,
        'Recommended size': state.result.recommendedSize,
        'Size taken': state.chosen.size,
        'Followed recommendation': state.chosen.size === state.result.recommendedSize ? 'yes' : 'no'
      };
      if (state.inputs.fit) props['Fit preference'] = fitLabel();

      QUESTIONS.forEach(function (question) {
        var key = question.inputKey;
        if (key === 'fit') return; // already written above, in its label form
        var value = state.inputs[key];
        if (value === null || value === undefined || value === '') return;
        var label = PROPERTY_LABELS[key] || (question.title || key);
        props[label] = PROPERTY_FORMATTERS[key] ? PROPERTY_FORMATTERS[key]() : String(value);
      });
      if (config.hasBuildOption) {
        props['Build'] = state.chosen.build;
        props['Recommended build'] = state.result.recommendedBuild;
      }
      var specced = measurementsForSize(state.chosen.size);
      if (specced.length) {
        specced.forEach(function (m) { props[m.label + ' (cm)'] = String(m.cm); });
      } else {
        Object.keys(MEASUREMENT_LABELS).forEach(function (key) {
          var cm = state.result.measurements[key];
          if (cm == null || isNaN(cm)) return;
          props[MEASUREMENT_LABELS[key] + ' (cm)'] = String(round1(cm));
        });
      }
      // Underscore prefix: hidden on cart/checkout in most themes, still on the order.
      props['_sizing_payload'] = JSON.stringify({
        inputs: state.inputs,
        result: state.result,
        v: 2
      });

      /* If nothing matches, stop and say so. This used to fall back to
       * "first available variant", which quietly added a size the customer
       * never chose - on a made-to-order coat at four figures with a twelve
       * week build and no resale route, that is the most expensive bug this
       * file could contain. A visible dead end is better than a silent wrong
       * order. */
      var variant = customVariant() || findVariant(state.chosen.size, state.chosen.build);
      if (!sellable(variant)) {
        state.variantError = config.hasBuildOption
          ? state.chosen.size + ' in ' + state.chosen.build + ' is not available. Adjust the size or length above, or book a call and we will sort it.'
          : state.chosen.size + ' is not available at the moment. Adjust the size above, or book a call and we will sort it.';
        render();
        return;
      }
      state.variantError = null;
      capture.send(ADD_STATUS, {
        path: 'find',
        inputs: capturedInputs(),
        result: state.result,
        chosenSize: state.chosen.size,
        chosenBuild: state.chosen.build,
        variantId: variant.id
      });
      addToCart(variant.id, props);
    }

    /* Human-readable line item property labels for the built-in inputKeys.
     * Anything not listed falls back to the question's own title, so a new
     * question still lands on the order legibly. */
    var PROPERTY_LABELS = {
      heightCm: 'Height',
      weightKg: 'Weight',
      shoeSizeEu: 'Shoe size (EU)',
      chestRange: 'Chest',
      collarIn: 'Collar (in)',
      waistIn: 'Waist (in)'
    };
    var PROPERTY_FORMATTERS = {
      heightCm: function () { return state.inputs.heightCm + ' cm'; },
      weightKg: function () { return state.inputs.weightKg + ' kg'; },
      chestRange: function () {
        /* The measurement alone. The size letter this range maps to used to
         * ride along here, and on the order beside "Recommended size" it read
         * as a second, sometimes contradictory recommendation. */
        return state.inputs.chestRange + '"';
      }
    };

    /* Shopify hides any line item property whose key starts with an underscore
     * from the cart, the order status page and the confirmation email, while
     * keeping it on the order for staff. Everything this panel gathers is
     * reference material for the fitting call, and two of the keys (Size,
     * Build) merely restate the variant's own options, so the customer should
     * see none of it.
     *
     * That prefixing used to happen in snippets/csf-private-properties.liquid,
     * which patched HTMLFormElement.prototype.submit globally to rewrite the
     * input names in the instant between them being written and the POST
     * leaving. It worked, but it put a patch on a DOM prototype for the life
     * of every page - I watched it intercept the Meta pixel's own tracking
     * form submissions on the product page. Doing it at the source, which that
     * snippet's own comment names as the proper fix, removes the global patch
     * entirely. The snippet is now a harmless no-op: it only rewrites keys
     * that do not already start with an underscore, and none do.
     */
    function addToCart(variantId, properties) {
      if (state.adding) return;

      if (WAITLIST) {
        /* Every path arrives here with the size it settled on: 'Size' on the
         * standard and compare paths, 'Size taken' on the measured one. */
        state.waitlistSize = properties['Size'] || properties['Size taken'] ||
          (state.chosen ? state.chosen.size : null);
        state.waitlistStatus = null;
        if (state.drawer) closeDrawer();
        goTo('waitlist');
        return;
      }

      var props = {};
      Object.keys(properties).forEach(function (key) {
        var value = properties[key];
        if (value === null || value === undefined) return;
        props[key.charAt(0) === '_' ? key : '_' + key] = String(value);
      });

      // The hidden form is kept in step so the fallback below posts the same
      // thing the request would have.
      variantInput.value = variantId;
      propsContainer.innerHTML = '';
      Object.keys(props).forEach(function (key) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + key + ']';
        input.value = props[key];
        propsContainer.appendChild(input);
      });

      /* The basket is shown here, in the panel, instead of on a new page. So
       * the add is a request rather than a form post, and the button that
       * started it says so while it is in flight - otherwise a second press
       * adds a second coat. */
      var layer = state.drawer ? drawerBody : body;
      var buttons = layer.querySelectorAll('.csf__next');
      var btn = buttons[buttons.length - 1] || null;
      var label = btn ? btn.textContent : '';
      state.adding = true;
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      var stale = layer.querySelector('[data-csf-add-error]');
      if (stale) stale.remove();

      var added = false;
      fetch(shopUrl('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: props }] })
      })
        .then(function (res) {
          return res.json().then(function (data) { return { ok: res.ok, data: data }; });
        })
        .then(function (r) {
          if (!r.ok) {
            // Shopify said no (sold out, usually). Say why, where they are.
            state.adding = false;
            if (btn && document.contains(btn)) { btn.disabled = false; btn.textContent = label; }
            var msg = el('p', 'csf__error',
              (r.data && (r.data.description || r.data.message)) ||
              'We could not add that to your basket. Please try again.');
            msg.setAttribute('role', 'alert');
            msg.setAttribute('data-csf-add-error', '');
            var row = btn && btn.closest('.csf__cta-row');
            if (row) row.parentNode.insertBefore(msg, row);
            else if (btn) btn.parentNode.insertBefore(msg, btn);
            return null;
          }
          added = true;
          return fetchCart().then(function (cart) {
            state.adding = false;
            state.cart = cart;
            state.basketError = null;
            if (state.drawer) closeDrawer();
            state.step = 'basket';
            state.history = [];
            panel.scrollTop = 0;
            renderWithFade();
            refreshCartBubble();
          });
        })
        .catch(function (err) {
          console.warn('[csf] in-panel add to basket failed, falling back:', err);
          state.adding = false;
          /* Never leave the customer with nothing. If the item went in but the
           * basket could not be read back, go to the cart page; posting the
           * form as well would add it twice. If it never went in, post the
           * form the way this always used to work. */
          if (added) window.location.href = shopUrl('cart');
          else form.submit();
        });
    }

    function shopUrl(path) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return root + path;
    }

    function fetchCart() {
      return fetch(shopUrl('cart.js'), { headers: { 'Accept': 'application/json' } })
        .then(function (res) {
          if (!res.ok) throw new Error('cart.js ' + res.status);
          return res.json();
        });
    }

    /* The header's basket count is server-rendered, so it is fetched again
     * through the Section Rendering API, the same way Dawn's own add to cart
     * refreshes it. Cosmetic: a failure here is ignored. */
    function refreshCartBubble() {
      var bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      fetch(shopUrl('?sections=cart-icon-bubble'))
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var html = data && data['cart-icon-bubble'];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.querySelector('.shopify-section');
          if (section) bubble.innerHTML = section.innerHTML;
        })
        .catch(function () { /* cosmetic */ });
    }

    /* ------------------------------------------------------------- basket */

    /* What the customer sees once something is in the basket: the lines, the
     * total, and the way on to checkout, without leaving the product page.
     *
     * Built from /cart.js rather than by borrowing the cart page's own
     * sections: those are laid out against the viewport width, and would
     * render their desktop table inside a 420px panel.
     *
     * Line item properties are not listed. Everything this panel writes is
     * underscore-prefixed, which is Shopify's convention for "staff only". */
    function renderBasket() {
      var cart = state.cart;
      var items = (cart && cart.items) || [];

      body.appendChild(el('div', 'csf__eyebrow', items.length ? 'Added to your basket' : 'Basket'));
      body.appendChild(el('h1', 'csf__title', 'Your basket'));

      if (!items.length) {
        body.appendChild(el('p', 'csf__hint', 'Your basket is empty.'));
        var only = el('button', 'csf__next', 'Continue shopping');
        only.type = 'button';
        only.addEventListener('click', close);
        body.appendChild(only);
        return;
      }

      var list = el('div', 'csf__basket-list');
      items.forEach(function (item) {
        var line = el('div', 'csf__basket-line');

        var src = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
        if (src) {
          var figure = el('div', 'csf__basket-figure');
          var img = document.createElement('img');
          img.src = sizedImage(src).replace('width=600', 'width=240');
          img.alt = (item.featured_image && item.featured_image.alt) || item.product_title || '';
          figure.appendChild(img);
          line.appendChild(figure);
        }

        var info = el('div', 'csf__basket-info');
        info.appendChild(el('span', 'csf__basket-title', item.product_title));
        var opts = (item.options_with_values || [])
          .filter(function (o) { return o.value && o.value !== 'Default Title'; })
          .map(function (o) { return o.name + ': ' + o.value; });
        if (opts.length) info.appendChild(el('span', 'csf__basket-meta', opts.join(' · ')));
        if (item.quantity > 1) info.appendChild(el('span', 'csf__basket-meta', 'Quantity: ' + item.quantity));

        var remove = el('button', 'csf__basket-remove', 'Remove');
        remove.type = 'button';
        remove.setAttribute('aria-label', 'Remove ' + item.product_title + ' from your basket');
        remove.addEventListener('click', function () {
          remove.disabled = true;
          fetch(shopUrl('cart/change.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          })
            .then(function (res) {
              if (!res.ok) throw new Error('cart/change.js ' + res.status);
              return res.json();
            })
            .then(function (next) {
              state.cart = next;
              state.basketError = null;
              render();
              refreshCartBubble();
            })
            .catch(function (err) {
              console.warn('[csf] remove from basket failed:', err);
              state.basketError = 'We could not remove that just now. Please try again.';
              render();
            });
        });
        info.appendChild(remove);
        line.appendChild(info);

        line.appendChild(el('span', 'csf__basket-price', formatMoney(item.final_line_price, config.moneyFormat)));
        list.appendChild(line);
      });
      body.appendChild(list);

      if (state.basketError) {
        var warn = el('p', 'csf__error', state.basketError);
        warn.setAttribute('role', 'alert');
        body.appendChild(warn);
      }

      var total = el('div', 'csf__basket-total');
      total.appendChild(el('span', '', 'Total'));
      total.appendChild(el('span', '', formatMoney(cart.total_price, config.moneyWithCurrencyFormat || config.moneyFormat)));
      body.appendChild(total);
      if (config.taxNote) body.appendChild(el('p', 'csf__basket-note', config.taxNote));

      var ctaRow = el('div', 'csf__cta-row');
      var keep = el('button', 'csf__cta-back', 'Continue shopping');
      keep.type = 'button';
      keep.addEventListener('click', close);
      var checkout = el('button', 'csf__next', config.checkoutLabel || 'Check out');
      checkout.type = 'button';
      checkout.addEventListener('click', function () {
        checkout.disabled = true;
        window.location.href = shopUrl('checkout');
      });
      ctaRow.appendChild(keep);
      ctaRow.appendChild(checkout);
      body.appendChild(ctaRow);
    }

    /* Shopify's money formats, e.g. "£{{amount}}" or "£{{amount}} GBP". The
     * format can carry markup; it is rendered as text, so tags are dropped. */
    function formatMoney(cents, format) {
      var fmt = String(format || '{{amount}}').replace(/<[^>]*>/g, '');
      function group(n, decimals, thousands, decimal) {
        var parts = (Number(n) / 100).toFixed(decimals).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.join(decimal);
      }
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
        switch (key) {
          case 'amount_no_decimals': return group(cents, 0, ',', '.');
          case 'amount_with_comma_separator': return group(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return group(cents, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return group(cents, 2, "'", '.');
          case 'amount_no_decimals_with_space_separator': return group(cents, 0, ' ', ',');
          case 'amount_with_space_separator': return group(cents, 2, ' ', ',');
          default: return group(cents, 2, ',', '.');
        }
      });
    }

    /* Stores the recommendation AND the choice. Saving only the recommendation
     * meant a customer who had deliberately moved to XL came back to "Use a
     * Saved Size" and was handed the L the algorithm had suggested, silently
     * undoing the decision they made last time. */
    function persistSavedSize(name) {
      var saved = loadJSON(LS_SIZES) || [];
      saved.push({
        name: name,
        inputs: state.inputs,
        result: state.result,
        chosen: state.chosen,
        createdAt: new Date().toISOString()
      });
      saveJSON(LS_SIZES, saved.slice(-5)); // keep last 5
    }

    /* ------------------------------------------------------------- helpers */

    // Shared select-then-confirm step: grid of value cells or a vertical
    // list of larger option cards, tracking a local selection until the
    // customer presses Next/ctaLabel - nothing here auto-advances on
    // click, deliberately (see file header comment).
    function choiceStep(opts) {
      if (opts.eyebrow) body.appendChild(el('div', 'csf__eyebrow', opts.eyebrow));
      body.appendChild(el('h1', 'csf__title', opts.title));
      if (opts.hint) body.appendChild(el('p', 'csf__hint', opts.hint));

      if (opts.unitToggle) {
        var toggle = el('div', 'csf__seg');
        opts.unitToggle.options.forEach(function (o) {
          var b = el('button', o.id === opts.unitToggle.active ? 'is-on' : '', o.label);
          b.type = 'button';
          b.addEventListener('click', function () { opts.unitToggle.onChange(o.id); });
          toggle.appendChild(b);
        });
        body.appendChild(toggle);
      }

      var selected = opts.initial != null ? opts.initial : null;
      var container = el('div', opts.layout === 'grid' ? 'csf__size-grid' : 'csf__choices');
      var itemEls = {};

      opts.options.forEach(function (o) {
        var item;
        if (opts.layout === 'grid') {
          item = el('button', 'csf__size-btn');
          item.type = 'button';
          item.appendChild(el('span', 'csf__size-label', String(o.label)));
          /* A second line on the button itself, so a screen that would
           * otherwise need a unit toggle can show both readings at once. */
          if (o.sub) item.appendChild(el('span', 'csf__size-sub', o.sub));
        } else {
          item = el('button', 'csf__choice');
          item.type = 'button';
          item.appendChild(el('span', 'csf__choice-title', o.label));
          if (o.sub) item.appendChild(el('span', 'csf__choice-sub', o.sub));
        }
        if (o.disabled) {
          item.disabled = true;
          item.classList.add('is-soldout');
        } else {
          item.addEventListener('click', function () {
            selected = o.value;
            Object.keys(itemEls).forEach(function (k) {
              itemEls[k].classList.toggle('is-selected', k === String(o.value));
            });
            updateCta();
          });
        }
        if (o.value === selected) item.classList.add('is-selected');
        itemEls[String(o.value)] = item;
        container.appendChild(item);
      });
      body.appendChild(container);

      var ctaRow = el('div', 'csf__cta-row');
      var backBtnFooter = el('button', 'csf__cta-back', 'Back');
      backBtnFooter.type = 'button';
      backBtnFooter.addEventListener('click', goBack);
      var nextBtn = el('button', 'csf__next', opts.ctaLabel || 'Next');
      nextBtn.type = 'button';
      nextBtn.addEventListener('click', function () {
        if (selected == null) return;
        opts.onNext(selected);
      });
      ctaRow.appendChild(backBtnFooter);
      ctaRow.appendChild(nextBtn);
      body.appendChild(ctaRow);

      updateCta();

      function updateCta() {
        var ready = selected != null;
        nextBtn.disabled = !ready;
        nextBtn.classList.toggle('is-disabled', !ready);
      }
    }

    function questionStep(opts) {
      body.appendChild(el('h1', 'csf__title', opts.title));
      if (opts.hint) body.appendChild(el('p', 'csf__hint', opts.hint));

      if (opts.unitToggle) {
        var toggle = el('div', 'csf__seg');
        opts.unitToggle.options.forEach(function (o) {
          var b = el('button', o.id === opts.unitToggle.active ? 'is-on' : '', o.label);
          b.type = 'button';
          b.addEventListener('click', function () { opts.unitToggle.onChange(o.id); });
          toggle.appendChild(b);
        });
        body.appendChild(toggle);
      }

      var fieldWrap = el('div', 'csf__fields');
      var inputs = {};
      opts.fields.forEach(function (f) {
        var field = el('div', 'csf__num-field');
        // Wraps just the input (not the unit label) so the CSS animated
        // focus underline has a positioning context sized exactly to the
        // input's own box - see .csf__num-input-wrap in custom-size-flow.css.
        var inputWrap = el('span', 'csf__num-input-wrap');
        var input = document.createElement('input');
        input.type = 'number';
        input.inputMode = 'decimal';
        input.step = 'any';
        input.className = 'csf__num-input';
        input.placeholder = f.placeholder;
        input.value = f.value;
        inputs[f.key] = input;
        inputWrap.appendChild(input);
        field.appendChild(inputWrap);
        field.appendChild(el('span', 'csf__unit-label', f.label));
        fieldWrap.appendChild(field);
      });
      body.appendChild(fieldWrap);

      var errEl = el('p', 'csf__error', '');
      errEl.hidden = true;
      body.appendChild(errEl);

      var ctaRow = el('div', 'csf__cta-row');
      var backBtnFooter = el('button', 'csf__cta-back', 'Back');
      backBtnFooter.type = 'button';
      backBtnFooter.addEventListener('click', goBack);
      var next = el('button', 'csf__next', 'Next');
      next.type = 'button';
      next.addEventListener('click', tryNext);
      ctaRow.appendChild(backBtnFooter);
      ctaRow.appendChild(next);
      body.appendChild(ctaRow);

      Object.keys(inputs).forEach(function (k) {
        inputs[k].addEventListener('keydown', function (e) {
          if (e.key === 'Enter') tryNext();
        });
      });

      function tryNext() {
        var values = {};
        Object.keys(inputs).forEach(function (k) { values[k] = inputs[k].value; });
        var error = opts.onNext(values);
        if (error) { errEl.textContent = error; errEl.hidden = false; }
      }
    }

    function standardSizeValues() {
      if (!config.hasSizeOption) return [];
      var seen = [];
      config.variants.forEach(function (v) {
        var value = v.options[config.sizeOptionIndex];
        if (value && value !== config.customVariantValue && seen.indexOf(value) === -1) seen.push(value);
      });
      return seen;
    }

    // Matches a variant on Size AND Build together (either side is skipped
    // when the product doesn't have that option, via hasSizeOption /
    // hasBuildOption) - matching on Size alone would silently pick
    // whichever Build happens to be first for that Size on products that
    // have both options.
    function findVariant(sizeValue, buildValue) {
      return config.variants.find(function (v) {
        var sizeOk = !config.hasSizeOption || v.options[config.sizeOptionIndex] === sizeValue;
        var buildOk = !config.hasBuildOption || v.options[config.buildOptionIndex] === buildValue;
        return sizeOk && buildOk;
      }) || null;
    }

    function customVariant() {
      if (!config.customVariantValue) return null;
      var v = findVariant(config.customVariantValue, state.chosen ? state.chosen.build : null);
      return sellable(v) ? v : null;
    }

    /* Shoe size conversions (men's, approximate): UK 8 = US 9 = EU 42 */
    function toEuShoe(n, unit) {
      if (unit === 'eu') return n;
      if (unit === 'uk') return Math.round((n + 34) * 2) / 2;
      if (unit === 'us') return Math.round((n + 33) * 2) / 2;
      return n;
    }
    function fromEuShoe(eu, unit) {
      if (unit === 'eu') return eu;
      if (unit === 'uk') return eu - 34;
      if (unit === 'us') return eu - 33;
      return eu;
    }

    function heightLabel() {
      if (state.units.height === 'cm') return state.inputs.heightCm + ' cm';
      var totalIn = state.inputs.heightCm / 2.54;
      return Math.floor(totalIn / 12) + "'" + Math.round(totalIn % 12) + '"';
    }
    function weightLabel() {
      return state.units.weight === 'kg'
        ? state.inputs.weightKg + ' kg'
        : Math.round(state.inputs.weightKg * 2.20462) + ' lbs';
    }
    function fitLabel() {
      return state.inputs.fit ? (state.inputs.fit.charAt(0).toUpperCase() + state.inputs.fit.slice(1)) : '';
    }

    function ladderRangeLabel(values) {
      if (!values.length) return '';
      return values.length > 1 ? values[0] + '–' + values[values.length - 1] : values[0];
    }
    function listWithOr(values) {
      if (!values || !values.length) return '';
      if (values.length === 1) return values[0];
      return values.slice(0, -1).join(', ') + ' or ' + values[values.length - 1];
    }
    function pad2(n) { return n < 10 ? '0' + n : String(n); }
    function fmtHalf(n) { return Number.isInteger(n) ? String(n) : Math.floor(n) + '½'; }

    /* Inch sizes shown in centimetres, to the nearest half, which is how a
     * shirt or trouser label is printed. The stored value stays in inches. */
    function inchesToCmLabel(inches) {
      return trimNum(round1(inches * 2.54));
    }
    function round1(n) { return Math.round(n * 2) / 2; }

    function el(tag, className, text) {
      var node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined && text !== '') node.textContent = text;
      return node;
    }

    function within(n, range) { return typeof n === 'number' && !isNaN(n) && n >= range[0] && n <= range[1]; }
    function limitText(key, unit) { var l = limitsFor(key); return l[0] + ' and ' + l[1] + ' ' + unit; }
    function loadJSON(key) {
      try { return JSON.parse(localStorage.getItem(key)); } catch (e) { return null; }
    }
    function saveJSON(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ }
    }
  }
})();

